export type Lang = "zh" | "en";

export const t = (lang: Lang, key: string): string => {
  const entry = translations[key];
  if (!entry) return key;
  return entry[lang] || entry.en || key;
};

const translations: Record<string, Record<Lang, string>> = {
  // Nav
  "nav.solution": { zh: "解决方案", en: "Solution" },
  "nav.how": { zh: "工作原理", en: "How It Works" },
  "nav.capabilities": { zh: "核心能力", en: "Capabilities" },
  "nav.control": { zh: "安全边界", en: "Control" },
  "nav.industries": { zh: "核心能力", en: "Capabilities" },
  "nav.supportedIndustries": { zh: "行业", en: "Industries" },
  "nav.pricing": { zh: "定价", en: "Pricing" },
  "nav.contact": { zh: "联系我们", en: "Contact Us" },
  "nav.compare": { zh: "对比", en: "Compare" },
  "nav.demo": { zh: "立即体验", en: "Try Live Demo" },
  "nav.cta": { zh: "下载", en: "Download" },

  // Try Live Demo
  "demo.notice": {
    zh: "本演示仅用于体验，请勿输入真实个人/医疗/保险/支付信息。",
    en: "This is a demo dental clinic. Please do not enter real personal, medical, insurance, or payment information.",
  },
  "demo.badge": { zh: "实时业务演示", en: "Live Workflow Demo" },
  "demo.preview.title": { zh: "体验一次真实的牙科预约演示", en: "Try a Live Dental Booking Demo" },
  "demo.preview.subtitle": {
    zh: "用文字、语音或表单提交预约需求，看 ClawEase 实时填写预约表单。",
    en: "Type, speak, or submit a form and watch ClawEase fill an appointment form in real time.",
  },
  "demo.preview.start": { zh: "开始体验", en: "Start Experience" },
  "demo.preview.textOption": { zh: "文本对话", en: "Text Chat" },
  "demo.preview.voiceOption": { zh: "语音拨打", en: "Voice Call" },
  "demo.preview.formOption": { zh: "表单提交", en: "Form Submission" },
  "demo.preview.connecting": { zh: "连接中", en: "Connecting" },
  "demo.preview.cardTitle": { zh: "体验说明", en: "Experience notes" },
  "demo.preview.point.1": { zh: "这是虚拟牙科诊所，不会创建真实预约。", en: "This is a fictional dental clinic and no real appointment is created." },
  "demo.preview.point.2": { zh: "请使用示例信息，不输入真实个人或医疗资料。", en: "Use sample information instead of real personal or medical details." },
  "demo.preview.point.3": { zh: "选择体验方式后才会建立演示连接。", en: "The demo connection starts only after you choose a demo mode." },
  "demo.preview.disclaimer": {
    zh: "为保护隐私，本页面只用于展示 ClawEase 的预约理解与自动填表流程；演示过程中请使用示例姓名、联系方式和预约需求，不要填写真实个人、医疗、保险或支付信息。",
    en: "To protect privacy, this page only demonstrates how ClawEase understands appointment requests and fills forms. Please use sample names, contact details, and booking needs instead of real personal, medical, insurance, or payment information.",
  },
  "demo.mode.eyebrow": { zh: "选择体验方式", en: "Choose a mode" },
  "demo.mode.title": { zh: "你想如何开始？", en: "How would you like to start?" },
  "demo.mode.subtitle": { zh: "你可以用文字、语音或表单提交触发同一套自动预约流程。", en: "Use text, voice, or form submission to trigger the same automated booking workflow." },
  "demo.mode.text": { zh: "文字模式", en: "Text Demo" },
  "demo.mode.text.desc": { zh: "最稳定的主路径，无需麦克风权限。", en: "The stable primary path, no microphone permission required." },
  "demo.mode.voice": { zh: "语音模式", en: "Voice Demo" },
  "demo.mode.voice.desc": { zh: "模拟真实通话链路，后续接火山 RTC。", en: "A mocked call path that can later connect to Volcengine RTC." },
  "demo.mode.form": { zh: "表单模式", en: "Form Demo" },
  "demo.mode.form.desc": { zh: "模拟用户从诊所官网提交预约表单。", en: "Simulate a visitor submitting the clinic website form." },
  "demo.form.panelTitle": { zh: "诊所官网预约", en: "Clinic Website" },
  "demo.form.website": { zh: "阳光牙科官网", en: "Sunny Dental Website" },
  "demo.form.websiteLine": { zh: "用户从官网点击预约，进入表单提交预约需求。", en: "A visitor clicks Book on the website and submits an appointment request." },
  "demo.form.bookCta": { zh: "预约", en: "Book" },
  "demo.form.title": { zh: "预约表单", en: "Appointment Form" },
  "demo.form.subtitle": { zh: "请使用示例信息完成一次表单提交预约。", en: "Use sample information to complete a form submission booking." },
  "demo.form.service": { zh: "服务类型", en: "Service Type" },
  "demo.form.doctor": { zh: "预约医生", en: "Doctor" },
  "demo.form.doctorSpecialties": { zh: "擅长领域", en: "Specialties" },
  "demo.form.time": { zh: "预约时间", en: "Appointment Time" },
  "demo.form.date": { zh: "预约日期", en: "Appointment Date" },
  "demo.form.clock": { zh: "具体时间", en: "Specific Time" },
  "demo.form.contact": { zh: "联系方式", en: "Contact" },
  "demo.form.note": { zh: "备注说明", en: "Notes" },
  "demo.form.contactHint": { zh: "可使用示例邮箱或手机号，不填写真实联系方式。", en: "Use a sample email or phone number. Do not enter real contact details." },
  "demo.form.safeHint": { zh: "提交后会建立演示连接，并由 ClawEase 同步到右侧预约系统。", en: "After submission, the demo connection starts and ClawEase syncs this into the booking system." },
  "demo.form.syncing": { zh: "ClawEase 正在同步到预约系统。", en: "ClawEase is syncing it to the booking system." },
  "demo.form.submit": { zh: "提交表单", en: "Submit" },
  "demo.form.submitted": { zh: "已提交", en: "Submitted" },
  "demo.form.default.service": { zh: "洗牙", en: "Dental Cleaning" },
  "demo.form.default.doctor": { zh: "李医生", en: "Dr. Li" },
  "demo.form.default.time": { zh: "下周二上午", en: "Next Tuesday Morning" },
  "demo.form.default.date": { zh: "下周二", en: "Next Tuesday" },
  "demo.form.default.email": { zh: "demo@sunnydental.example", en: "demo@sunnydental.example" },
  "demo.form.default.note": { zh: "首次检查。", en: "First check." },
  "demo.form.option.cleaning": { zh: "洗牙", en: "Dental Cleaning" },
  "demo.form.option.exam": { zh: "牙科检查", en: "Dental Exam" },
  "demo.form.option.consult": { zh: "牙科咨询", en: "Dental Consultation" },
  "demo.text.title": { zh: "AI 前台 · 文字", en: "AI Receptionist · Text" },
  "demo.text.placeholder": {
    zh: "Type your message...",
    en: "Type your message...",
  },
  "demo.text.send": { zh: "发送", en: "Send" },
  "demo.text.retry": { zh: "重试", en: "Retry" },
  "demo.text.switchVoice": { zh: "切换到语音拨打", en: "Switch to voice call" },
  "demo.text.emptyError": { zh: "请输入预约需求", en: "Please enter a booking request." },
  "demo.text.chip.1": {
    zh: "我想预约下周二上午洗牙。",
    en: "I'd like to book a cleaning next Tuesday morning.",
  },
  "demo.text.chip.2": {
    zh: "牙疼得厉害，今天下午能尽快安排吗？",
    en: "I have a bad toothache. Is there any availability this afternoon?",
  },
  "demo.text.chip.3": {
    zh: "想咨询补牙，先帮我约个初诊。",
    en: "I'd like to ask about fillings and book an initial visit.",
  },
  "demo.voice.title": { zh: "AI 前台 · 语音", en: "AI Receptionist · Voice" },
  "demo.voice.status.idle": { zh: "点击拨打", en: "Tap to call" },
  "demo.voice.status.requesting": { zh: "请求麦克风权限", en: "Requesting microphone" },
  "demo.voice.status.connecting": { zh: "接通中", en: "Connecting" },
  "demo.voice.status.connected": { zh: "AI 正在说话", en: "AI Speaking" },
  "demo.voice.status.ended": { zh: "通话结束", en: "Call ended" },
  "demo.voice.status.error": { zh: "语音连接失败", en: "Voice connection failed" },
  "demo.voice.mockHint": { zh: "接通后，请使用示例预约需求开始对话。", en: "After the call connects, use a sample booking request to start." },
  "demo.voice.call": { zh: "拨打", en: "Call" },
  "demo.voice.end": { zh: "挂断", en: "End" },
  "demo.voice.mute": { zh: "静音", en: "Mute" },
  "demo.voice.unmute": { zh: "取消静音", en: "Unmute" },
  "demo.voice.switchText": { zh: "切换到文本对话", en: "Switch to text chat" },
  "demo.switchBlocked.title": { zh: "当前会话已开始", en: "Session already started" },
  "demo.switchBlocked.body": { zh: "发送文本或开启语音拨打后，不能直接切换体验方式。请先结束当前会话后再切换。", en: "After sending a text message or starting a voice call, you cannot switch modes directly. Please end the current session before switching." },
  "demo.switchBlocked.ok": { zh: "知道了", en: "Got it" },
  "demo.switchBlocked.end": { zh: "结束会话", en: "End session" },
  "demo.conversation.empty": { zh: "和ClawEase建立连接后，即可开始对话。", en: "After choosing a mode, the AI receptionist starts the conversation here." },
  "demo.conversation.thinking": { zh: "正在整理回复", en: "Thinking" },
  "demo.input.privacyHint": { zh: "演示提示：请勿输入真实个人、医疗、保险或支付信息。", en: "Demo note: do not enter real personal, medical, insurance, or payment information." },
  "demo.reception.title": { zh: "AI 前台", en: "AI Receptionist" },
  "demo.reception.online": { zh: "在线", en: "Online" },
  "demo.connection.connecting": { zh: "正在建立演示连接", en: "Connecting demo session" },
  "demo.connection.connected": { zh: "演示连接已建立", en: "Demo connection established" },
  "demo.connection.disconnected": { zh: "演示连接未建立", en: "Demo connection disconnected" },
  "demo.control.empty": { zh: "等待 ClawEase 接收用户需求。", en: "Waiting for ClawEase to receive the request." },
  "demo.control.connecting": { zh: "建立连接中", en: "Connecting" },
  "demo.control.waitingNext": { zh: "等待下一步", en: "Waiting for next step" },
  "demo.control.connected": { zh: "建立连接", en: "Connection established" },
  "demo.control.intent": { zh: "识别用户意图", en: "Detected user intent" },
  "demo.control.intentAgain": { zh: "重新识别用户意图", en: "Redetected user intent" },
  "demo.control.openSystem": { zh: "打开预约系统", en: "Opened appointment system" },
  "demo.control.openSystemAgain": { zh: "重新打开预约系统", en: "Reopened appointment system" },
  "demo.control.recognize": { zh: "识别", en: "Detected" },
  "demo.control.recognizeAgain": { zh: "重新识别", en: "Redetected" },
  "demo.control.extractInfo": { zh: "提取预约信息", en: "Extract appointment details" },
  "demo.control.fillForm": { zh: "填写预约表单", en: "Fill appointment form" },
  "demo.control.submitRequest": { zh: "提交预约请求", en: "Submit appointment request" },
  "demo.control.extract": { zh: "提取", en: "Extracted" },
  "demo.control.fill": { zh: "填充", en: "Filled" },
  "demo.control.ready": { zh: "校验预约字段", en: "Validated appointment fields" },
  "demo.control.submitting": { zh: "正在提交预约请求", en: "Submitting appointment request" },
  "demo.control.submittingAgain": { zh: "重新提交预约请求", en: "Resubmitting appointment request" },
  "demo.control.submitted": { zh: "预约请求创建成功", en: "Appointment request created" },
  "demo.control.completed": { zh: "完成预约", en: "Appointment completed" },
  "demo.control.failed": { zh: "预约请求提交失败", en: "Appointment submission failed" },
  "demo.control.error": { zh: "执行异常", en: "Execution error" },
  "demo.control.voiceConnected": { zh: "建立语音通话", en: "Voice call connected" },
  "demo.control.callUpdated": { zh: "更新通话状态", en: "Updated call state" },
  "demo.extract.title": { zh: "实时提取信息", en: "Live Extraction" },
  "demo.extract.summaryTitle": { zh: "已提取信息", en: "Extracted information" },
  "demo.extract.status.waiting": { zh: "等待预约需求", en: "Waiting for a booking request" },
  "demo.extract.status.intent": { zh: "正在识别预约意图", en: "Identifying booking intent" },
  "demo.extract.status.extracting": { zh: "正在提取预约信息", en: "Extracting appointment details" },
  "demo.extract.status.complete": { zh: "信息已完整", en: "Required details complete" },
  "demo.extract.status.done": { zh: "体验完成", en: "Demo complete" },
  "demo.extract.status.error": { zh: "提取异常", en: "Extraction error" },
  "demo.extract.intent": { zh: "意图", en: "Intent" },
  "demo.extract.intent.booking": { zh: "牙科预约", en: "Dental booking" },
  "demo.extract.empty": { zh: "待提取", en: "Waiting" },
  "demo.steps.collect": { zh: "对话采集", en: "Collect" },
  "demo.steps.extract": { zh: "信息提取", en: "Extract" },
  "demo.steps.fill": { zh: "填写系统", en: "Fill" },
  "demo.steps.submit": { zh: "提交完成", en: "Submit" },
  "demo.steps.current": { zh: "当前", en: "Current" },
  "demo.console.title": { zh: "阳光诊所预约", en: "Sunny Clinic Booking" },
  "demo.console.status": { zh: "当前状态", en: "Status" },
  "demo.console.status.waiting": { zh: "等待预约意图", en: "Waiting for booking intent" },
  "demo.console.status.opening": { zh: "正在打开预约表单", en: "Opening appointment form" },
  "demo.console.status.filling": { zh: "正在填写字段", en: "Filling appointment fields" },
  "demo.console.status.ready": { zh: "准备提交", en: "Ready to submit" },
  "demo.console.status.submitting": { zh: "提交中", en: "Submitting" },
  "demo.console.status.submitted": { zh: "已创建预约请求", en: "Appointment request created" },
  "demo.console.status.failed": { zh: "提交失败", en: "Submit failed" },
  "demo.browser.tabTitle": { zh: "预约系统", en: "Appointment System" },
  "demo.browser.clinicName": { zh: "阳光牙科诊所", en: "Sunny Dental Clinic" },
  "demo.browser.secure": { zh: "安全连接", en: "Secure" },
  "demo.browser.addressPlaceholder": { zh: "等待输入系统地址", en: "Waiting for system URL" },
  "demo.browser.blank": { zh: "等待 ClawEase 打开预约系统。", en: "Waiting for ClawEase to open the appointment system." },
  "demo.browser.opening": { zh: "ClawEase 正在输入预约系统地址并打开页面。", en: "ClawEase is entering the appointment system URL." },
  "demo.browser.ready": { zh: "预约系统已打开，等待 ClawEase 填写字段。", en: "Appointment system opened. Waiting for ClawEase to fill fields." },
  "demo.browser.waitingForm": { zh: "表单将在识别到预约需求后自动填写。", en: "The form will be filled automatically after ClawEase identifies the request." },
  "demo.browser.formTitle": { zh: "新建预约表单", en: "New Appointment Form" },
  "demo.browser.autoFill": { zh: "由 ClawEase 自动填写", en: "Autofilled by ClawEase" },
  "demo.browser.draft": { zh: "草稿", en: "Draft" },
  "demo.field.service_type": { zh: "服务类型", en: "Service Type" },
  "demo.field.patient_type": { zh: "患者类型", en: "Patient Type" },
  "demo.field.preferred_date": { zh: "预约日期", en: "Preferred Date" },
  "demo.field.preferred_time": { zh: "预约时间", en: "Preferred Time" },
  "demo.field.appointment_time": { zh: "预约时间", en: "Appointment Time" },
  "demo.field.appointment_doctor": { zh: "预约医生", en: "Appointment Doctor" },
  "demo.field.doctor": { zh: "预约医生", en: "Doctor" },
  "demo.field.patient_name": { zh: "患者姓名", en: "Patient Name" },
  "demo.field.contact": { zh: "联系方式", en: "Contact Method" },
  "demo.field.contact_phone": { zh: "联系方式", en: "Contact Method" },
  "demo.field.notes": { zh: "备注说明", en: "Notes" },
  "demo.field.note": { zh: "备注说明", en: "Notes" },
  "demo.field.status": { zh: "状态", en: "Status" },
  "demo.default.patientName": { zh: "演示患者", en: "Demo Patient" },
  "demo.default.doctor": { zh: "李医生", en: "Dr. Li" },
  "demo.default.contact": { zh: "演示联系方式", en: "Demo contact" },
  "demo.emptyValue": { zh: "—", en: "—" },
  "demo.success.title": { zh: "预约请求已创建", en: "Appointment Request Created" },
  "demo.success.requestId": { zh: "请求编号", en: "Request ID" },
  "demo.success.preferredTime": { zh: "预约时间", en: "Preferred Time" },
  "demo.success.submitted": { zh: "已提交", en: "Submitted" },
  "demo.submit.idle": { zh: "提交预约", en: "Submit" },
  "demo.submit.submitting": { zh: "提交中", en: "Submitting" },
  "demo.submit.submitted": { zh: "已提交", en: "Submitted" },
  "demo.email.sent": { zh: "已发送邮件", en: "Email sent" },
  "demo.activity.title": { zh: "系统活动", en: "System Activity" },
  "demo.activity.empty": { zh: "等待 AI 识别预约需求。", en: "Waiting for the AI to identify a booking request." },
  "demo.activity.intent": { zh: "已识别预约意图", en: "Booking intent detected" },
  "demo.activity.opening": { zh: "已打开预约表单", en: "Appointment form opened" },
  "demo.activity.ready": { zh: "核心字段已齐全", en: "Required details are complete" },
  "demo.activity.submitting": { zh: "正在提交预约请求", en: "Submitting appointment request" },
  "demo.activity.submitted": { zh: "已提交", en: "Submitted" },
  "demo.activity.error": { zh: "出现异常", en: "Error received" },
  "demo.activity.call": { zh: "语音通话状态已更新", en: "Voice call state updated" },
  "demo.outro.badge": { zh: "演示流程已完成", en: "Demo workflow completed" },
  "demo.outro.title": { zh: "刚才这套流程，也可以跑在你的业务系统里", en: "This Workflow Can Also Run in Your Business System" },
  "demo.outro.workflowTitle": { zh: "刚才 ClawEase 已完成：", en: "ClawEase just completed:" },
  "demo.outro.workflow.1": { zh: "响应客户预约请求", en: "Answered the customer request" },
  "demo.outro.workflow.2": { zh: "识别预约意图", en: "Identified the appointment intent" },
  "demo.outro.workflow.3": { zh: "收集必要预约信息", en: "Collected required details" },
  "demo.outro.workflow.4": { zh: "填写预约表单", en: "Filled the appointment form" },
  "demo.outro.workflow.5": { zh: "提交预约请求", en: "Submitted the request" },
  "demo.outro.subtitle": { zh: "这只是一次虚拟牙科预约演示。下一步，我们可以一起看看 ClawEase 如何连接你的真实预约系统、CRM 或业务后台，把客户请求自动转化为业务结果。", en: "This was only a virtual dental booking demo. Next, we can look together at how ClawEase connects to your real booking system, CRM, or business backend, and turns customer requests into business outcomes." },
  "demo.outro.cta": { zh: "预约产品演示", en: "Book a Product Demo" },
  "demo.outro.tryAgain": { zh: "再试一次", en: "Try Again" },
  "demo.outro.note": { zh: "无需替换现有系统，先从一个高频流程开始验证。", en: "No need to replace your existing system. Start by validating one high-frequency workflow." },

  // Hero
  "hero.badge": { zh: "面向高价值请求的 AI接待员", en: "AI Receptionist That Gets Things Done" },
  "hero.title1": { zh: "把客户咨询转化为", en: "Turn customer inquiries into" },
  "hero.rotate.1": { zh: "完成的业务流程", en: "completed workflows" },
  "hero.rotate.2": { zh: "确认的预约", en: "confirmed bookings" },
  "hero.rotate.3": { zh: "成交的订单", en: "closed deals" },
  "hero.rotate.4": { zh: "真实的收入", en: "real revenue" },
  "hero.sub": {
    zh: "你的 AI 客服，通过电话、聊天和邮件解答客户问题、安排预约并跟进后续需求，帮助团队将客户咨询转化为确认的预约。",
    en: "Your AI Receptionist answers customer questions, books appointments, and handles follow-ups across phone, chat, and email, helping your team turn inquiries into confirmed bookings.",
  },
  "hero.sub.role": { zh: "你的 AI 客服", en: "Your AI Receptionist" },
  "hero.sub.description": {
    zh: "，通过电话、聊天和邮件解答客户问题、安排预约并跟进后续需求，帮助团队将客户咨询转化为确认的预约。",
    en: " answers customer questions, books appointments, and handles follow-ups across phone, chat, and email, helping your team turn inquiries into confirmed bookings.",
  },
  "hero.demo.email": { zh: "你的工作邮箱？", en: "What's your work email?" },
  "hero.demo.button": { zh: "联系我们", en: "Contact Us" },
  "hero.demo.phone": { zh: "Phone", en: "Phone" },
  "hero.demo.line": { zh: "LINE", en: "LINE" },
  "hero.demo.whatsapp": { zh: "WhatsApp", en: "WhatsApp" },
  "hero.demo.emailInput": { zh: "Email", en: "Email" },
  "hero.demo.forms": { zh: "Forms", en: "Forms" },
  "hero.demo.sms": { zh: "SMS", en: "SMS" },
  "hero.cta.primary": { zh: "免费试用", en: "Get started for free" },
  "hero.cta.secondary": { zh: "了解工作方式", en: "See how it works" },

  // PainPoints
  "pain.aria": { zh: "痛点焦虑", en: "Pain points" },
  "pain.badge": { zh: "正在流失的高价值请求", en: "High-value requests are slipping away" },
  "pain.title": { zh: "每个未响应的渠道，\n都是流失的订单", en: "Every unresponded channel\nis a lost order." },
  "pain.subtitle": {
    zh: "客户已经联系了你 —— 在电话里、在 WhatsApp 上、在网站表单里。但人手有限，响应不过来。",
    en: "Customers already reached out — by phone, on WhatsApp, through your web form. Your team just can't keep up.",
  },
  "pain.stat.1.num": { zh: "38", en: "38" },
  "pain.stat.1.suffix": { zh: "%", en: "%" },
  "pain.stat.1.label": { zh: "来电高峰时段无人接听", en: "Calls missed during peak hours" },
  "pain.stat.1.source": { zh: "行业平均 · 美容医疗类", en: "Industry avg · Aesthetic & medical" },
  "pain.stat.2.num": { zh: "24", en: "24" },
  "pain.stat.2.suffix": { zh: "h+", en: "h+" },
  "pain.stat.2.label": { zh: "网页表单平均跟进时长", en: "Avg. web form follow-up time" },
  "pain.stat.2.source": { zh: "客户调研 · 中小机构样本", en: "Customer survey · SMB sample" },
  "pain.stat.3.num": { zh: "70", en: "70" },
  "pain.stat.3.suffix": { zh: "%", en: "%" },
  "pain.stat.3.label": { zh: "错过响应客户不再回拨", en: "Of missed leads never call back" },
  "pain.stat.3.source": { zh: "公开行业研究", en: "Public industry research" },
  "pain.hint": { zh: "ClawEase 让这三个数字归零", en: "ClawEase brings all three to zero" },

  // Meet ClawEase
  "meet.aria": { zh: "认识 ClawEase", en: "Meet ClawEase" },
  "meet.badge": { zh: "价值主张", en: "Value Proposition" },
  "meet.title": { zh: "认识 ClawEase", en: "Meet ClawEase" },
  "meet.subtitle": {
    zh: "不是聊天机器人，不是 IVR，是会替你跑业务系统的 AI 操作员。",
    en: "Not a chatbot. Not an IVR. An AI business operator that runs your web systems for you.",
  },
  "meet.center.role": { zh: "AI 业务操作员", en: "AI Business Operator" },
  "meet.center.pill.1": { zh: "7×24 不休不眠", en: "24/7 always on" },
  "meet.center.pill.2": { zh: "全渠道在线", en: "Every channel live" },
  "meet.cap.1.title": { zh: "自主学习", en: "Auto-learning" },
  "meet.cap.1.desc": { zh: "主动探索网页系统，记住完整路径", en: "Explores your web system and remembers the full path" },
  "meet.cap.2.title": { zh: "多渠道", en: "Multi-channel" },
  "meet.cap.2.desc": { zh: "接住电话、LINE、WhatsApp、Email、表单", en: "Connects calls, LINE, WhatsApp, email, and forms" },
  "meet.cap.3.title": { zh: "意图识别", en: "Intent recognition" },
  "meet.cap.3.desc": { zh: "理解上下文，提取关键业务信息", en: "Understands context and extracts business details" },
  "meet.cap.4.title": { zh: "自动执行", en: "Auto-execution" },
  "meet.cap.4.desc": { zh: "操作现有网页系统完成业务动作", en: "Operates existing web systems to complete work" },
  "meet.cap.5.title": { zh: "自动反馈", en: "Auto-feedback" },
  "meet.cap.5.desc": { zh: "同步执行结果、状态和业务轨迹", en: "Syncs results, status, and execution traces" },
  "meet.cap.6.title": { zh: "转人工", en: "Human handoff" },
  "meet.cap.6.desc": { zh: "异常或超出边界时，交由人工接管", en: "Hands off to people when exceptions occur" },
  "meet.boundary": { zh: "也知边界：超出授权，主动转人工", en: "It also knows boundaries: hand off when authority is exceeded" },
  "meet.metric.1.value": { zh: "24/7", en: "24/7" },
  "meet.metric.1.label": { zh: "全天在线", en: "ALWAYS ON" },
  "meet.metric.1.sub": { zh: "深夜高峰持续响应", en: "NIGHTS AND PEAK HOURS COVERED" },
  "meet.metric.2.value": { zh: "0%", en: "0%" },
  "meet.metric.2.label": { zh: "漏接率", en: "MISSED RATE" },
  "meet.metric.2.sub": { zh: "关键请求自动接住", en: "CRITICAL REQUESTS CAPTURED" },
  "meet.metric.3.value": { zh: "6+", en: "6+" },
  "meet.metric.3.label": { zh: "接入渠道", en: "CONNECTED CHANNELS" },
  "meet.metric.3.sub": { zh: "电话、即时通讯、邮箱、表单、短信", en: "PHONE, LINE, WHATSAPP, EMAIL, FORMS, AND SMS" },
  "meet.metric.4.value": { zh: ">99%", en: ">99%" },
  "meet.metric.4.label": { zh: "自动执行率", en: "AUTO-EXECUTION RATE" },
  "meet.metric.4.sub": { zh: "网页操作稳定完成", en: "WEB ACTIONS COMPLETED RELIABLY" },

  // Templates
  "templates.label": { zh: "开箱即用的工作流模板", en: "Ready-to-use workflow templates" },
  "templates.title": { zh: "从你行业的工作流开始", en: "Start with a workflow built for your industry" },
  "templates.aesthetics.title": { zh: "医美工作流", en: "Medical Aesthetics Workflow" },
  "templates.aesthetics.desc": { zh: "处理咨询、预约、改期和术前表单——全部通过浏览器自动化完成。", en: "Handle consultation, booking, rescheduling, and pre-op forms — all automated through browser workflows." },
  "templates.aesthetics.f1": { zh: "项目与价格咨询", en: "Procedure & pricing consultation" },
  "templates.aesthetics.f2": { zh: "按时间/医生偏好预约", en: "Booking with time/practitioner preference" },
  "templates.aesthetics.f3": { zh: "术前表单收集与提交", en: "Pre-op form collection & submission" },
  "templates.aesthetics.f4": { zh: "术后回访排程", en: "Post-op follow-up scheduling" },
  "templates.dental.title": { zh: "牙科诊所工作流", en: "Dental Clinic Workflow" },
  "templates.dental.desc": { zh: "从初诊咨询到复诊提醒——自动采集病史、保险信息并预约挂号。", en: "From initial inquiry to recall reminders — capture patient history, insurance info, and book appointments automatically." },
  "templates.dental.f1": { zh: "新患者建档（病史+保险）", en: "New patient intake with history & insurance" },
  "templates.dental.f2": { zh: "预约挂号与改期", en: "Appointment booking & rescheduling" },
  "templates.dental.f3": { zh: "自动复诊提醒系统", en: "Automated recall & reminder system" },
  "templates.dental.f4": { zh: "术后护理指导", en: "Post-treatment care instructions" },
  "templates.pet.title": { zh: "宠物服务工作流", en: "Pet Services Workflow" },
  "templates.pet.desc": { zh: "承接美容、寄养、疫苗咨询——采集宠物档案并无缝排期。", en: "Capture grooming, boarding, and vaccination inquiries — collect pet profiles and schedule services seamlessly." },
  "templates.pet.f1": { zh: "服务咨询与可用性查询", en: "Service inquiry & availability check" },
  "templates.pet.f2": { zh: "宠物档案与病史采集", en: "Pet profile & medical history capture" },
  "templates.pet.f3": { zh: "接送排程", en: "Drop-off / pickup scheduling" },
  "templates.pet.f4": { zh: "服务后回访与复购", en: "Post-service follow-up & rebooking" },

  // Problem / Solution
  "ps.label": { zh: "问题与方案", en: "The Problem & The Solution" },
  "ps.title": { zh: "真实团队正在经历的困境", en: "What breaks in real teams" },
  "ps.problem.title": { zh: "痛点", en: "What breaks" },
  "ps.problem.1": { zh: "下班后电话无人接听", en: "After-hours calls go unanswered" },
  "ps.problem.2": { zh: "前台忙不过来，漏接不断", en: "Front desk too busy — missed calls pile up" },
  "ps.problem.3": { zh: "手工录入网页系统，慢且易出错", en: "Manual data entry into web systems — slow and error-prone" },
  "ps.problem.4": { zh: "不同员工处理方式不统一", en: "Inconsistent handling across different staff" },
  "ps.problem.5": { zh: "咨询结束无人跟进", en: "No follow-up after inquiry ends" },
  "ps.problem.6": { zh: "高价值客户流向响应更快的竞争对手", en: "High-value customers slip away to faster competitors" },
  "ps.solution.title": { zh: "ClawEase 如何解决", en: "How ClawEase solves it" },
  "ps.solution.1": { zh: "24/7 全渠道 AI 接管——电话、WhatsApp、LINE、表单", en: "24/7 AI takeover — phone, WhatsApp, LINE, forms, email" },
  "ps.solution.2": { zh: "无限并发，永不占线", en: "Infinite concurrency — never a busy signal" },
  "ps.solution.3": { zh: "浏览器自动操作网页系统，零手工", en: "Browser automation operates web systems — zero manual entry" },
  "ps.solution.4": { zh: "标准化流程，每次服务一致", en: "Standardized process — consistent service every time" },
  "ps.solution.5": { zh: "自动生成任务并通知团队", en: "Auto-generated tasks with team notifications" },
  "ps.solution.6": { zh: "即时响应 + 自动闭环 = 永远快人一步", en: "Instant response + auto-close = always one step ahead" },

  // How It Works
  "how.label": { zh: "工作方式", en: "How It Works" },
  "how.title": { zh: "从咨询到结果——自动完成", en: "From inquiry to result — automatically" },
  "how.step1.title": { zh: "捕获", en: "Capture" },
  "how.step1.desc": { zh: "电话、LINE、WhatsApp、Email、表单——全渠道 24/7", en: "Phone, LINE, WhatsApp, Email, forms — every channel, 24/7" },
  "how.step2.title": { zh: "理解", en: "Understand" },
  "how.step2.desc": { zh: "AI 识别意图——预约、咨询、改期还是查询", en: "AI recognizes intent — booking, inquiry, reschedule, or status check" },
  "how.step3.title": { zh: "采集", en: "Collect" },
  "how.step3.desc": { zh: "结构化信息采集——姓名、服务、时间、偏好", en: "Structured data capture — name, service, time, preferences" },
  "how.step4.title": { zh: "执行", en: "Execute" },
  "how.step4.desc": { zh: "浏览器自动化操作你的网页系统——无需 API", en: "Browser automation operates your web systems — no API needed" },
  "how.step5.title": { zh: "闭环", en: "Close" },
  "how.step5.desc": { zh: "预约已创建，团队已通知——业务闭环完成", en: "Booking created, team notified — business loop closed" },

  // How It Works v2
  "howItWorks.badge": { zh: "工作方式", en: "How It Works" },
  "howItWorks.title": {
    zh: "从客户开口咨询到自动完成预约，一步到位",
    en: "From customer inquiry to completed booking, all in one step.",
  },
  "howItWorks.sub": {
    zh: "ClawEase 完美模拟真实人工操作：从倾听客户意图到咨询回复、网页填表、提交，全流程自动化闭环，无需API改造，用最轻量级的接入方式，帮你跑通最复杂的业务流程。",
    en: "ClawEase faithfully simulates real staff operations: listening to customer intent, answering inquiries, filling web forms, and submitting requests. It automates the full workflow end to end with no API rebuild, using the lightest integration path to help you run even the most complex business processes.",
  },
  "howItWorks.steps.1.label": { zh: "STEP 1：自主学习", en: "STEP 1: Autonomous Learning" },
  "howItWorks.steps.1.title": { zh: "它会学", en: "It can learn" },
  "howItWorks.steps.1.desc": {
    zh: "ClawEase 能够像人类员工一样主动探索你的网页系统，只需探索一次，即可精准记忆导航、填表、提交的完整路径，并生成自动化工作流，无需 API 对接，无需系统改造。",
    en: "ClawEase can actively explore your web system like a human employee. Explore it once, and it precisely remembers the full path for navigation, form filling, and submission, then generates an automated workflow. No API integration. No system changes.",
  },
  "howItWorks.steps.1.tag.1": { zh: "无需 API", en: "No API" },
  "howItWorks.steps.1.tag.2": { zh: "无需改代码", en: "No code changes" },
  "howItWorks.steps.1.tag.3": { zh: "原生兼容任意网页", en: "Works natively with any webpage" },
  "howItWorks.steps.2.label": { zh: "STEP 2：全渠道接入", en: "STEP 2: Omnichannel Intake" },
  "howItWorks.steps.2.title": { zh: "它会听", en: "It can listen" },
  "howItWorks.steps.2.desc": {
    zh: "将电话、LINE、WhatsApp、Email、表单等零散入口统一接入 AI 中枢，24/7 全天候在线响应，确保在任何深夜和高峰期，高价值请求绝不漏接。",
    en: "Connect scattered entry points like phone, LINE, WhatsApp, email, and forms into one AI hub with 24/7 always-on response, ensuring high-value requests are never missed during late nights or peak hours.",
  },
  "howItWorks.steps.2.tag.1": { zh: "多渠道", en: "Multi-channel" },
  "howItWorks.steps.2.tag.2": { zh: "24/7 在线", en: "24/7 online" },
  "howItWorks.steps.2.tag.3": { zh: "实时响应", en: "Real-time response" },
  "howItWorks.steps.2.tag.4": { zh: "不漏接", en: "Never misses" },
  "howItWorks.steps.3.label": { zh: "STEP 3：意图识别", en: "STEP 3: Intent Recognition" },
  "howItWorks.steps.3.title": { zh: "它会懂", en: "It can understand" },
  "howItWorks.steps.3.desc": {
    zh: "拒绝死板的关键词匹配，ClawEase 能够完美理解复杂的上下文对话，并严格按照你的业务规则，自动追问并精准提取时间、日期等结构化核心信息。",
    en: "ClawEase goes beyond rigid keyword matching. It understands complex conversational context, follows your business rules, automatically asks follow-up questions, and precisely extracts structured core information such as time and date.",
  },
  "howItWorks.steps.3.tag.1": { zh: "意图识别", en: "Intent recognition" },
  "howItWorks.steps.3.tag.2": { zh: "自然对话", en: "Natural conversation" },
  "howItWorks.steps.3.tag.3": { zh: "动态追问", en: "Dynamic follow-up" },
  "howItWorks.steps.3.tag.4": { zh: "结构化信息提取", en: "Structured information extraction" },
  "howItWorks.steps.4.label": { zh: "STEP 4：自动执行", en: "STEP 4: Automated Execution" },
  "howItWorks.steps.4.title": { zh: "它会做", en: "It can execute" },
  "howItWorks.steps.4.desc": {
    zh: "像训练有素的熟手员工一样操作你的现有系统，代替人工进行点击、选择、填表和提交，在不改变你任何现有软件的前提下，完成业务短咨询、预约等业务场景的闭环。",
    en: "It operates your existing systems like a skilled staff member, replacing manual clicking, selecting, form filling, and submission. Without changing any of your current software, it closes the loop for quick consultations, bookings, and other business scenarios.",
  },
  "howItWorks.steps.4.tag.1": { zh: "零侵入式操作", en: "Non-invasive operation" },
  "howItWorks.steps.4.tag.2": { zh: "免API原生网页执行", en: "No-API web-native execution" },
  "howItWorks.steps.4.tag.3": { zh: "支持人工随时介入", en: "Human takeover anytime" },
  "howItWorks.steps.5.label": { zh: "STEP 5：自动反馈", en: "STEP 5: Automatic Feedback" },
  "howItWorks.steps.5.title": { zh: "它会反馈", en: "It can report back" },
  "howItWorks.steps.5.desc": {
    zh: "告别黑盒操作，ClawEase 实时反馈执行结果，看板一目了然：自动捕获、自动填写、自动提交，每一步都有明确的执行轨迹与摘要推送，让自动化执行结果一目了然。",
    en: "No more black-box automation. ClawEase feeds back execution results in real time through a clear dashboard: automatic capture, automatic form filling, and automatic submission, with a clear execution trail and summary push for every step so automation outcomes are easy to understand at a glance.",
  },
  "howItWorks.steps.5.tag.1": { zh: "状态自动对齐", en: "Automatic status alignment" },
  "howItWorks.steps.5.tag.2": { zh: "免人工核对", en: "No manual checking" },
  "howItWorks.steps.5.tag.3": { zh: "可视化执行轨迹", en: "Visual execution trail" },
  "howItWorks.visual.browser.search": { zh: "搜索或输入地址", en: "Search or enter address" },
  "howItWorks.visual.learn.bookingSystem": { zh: "预约系统", en: "Booking system" },
  "howItWorks.visual.learn.status": { zh: "自主探索中...", en: "Exploring autonomously..." },
  "howItWorks.visual.learn.plugin": { zh: "ClawEase 插件", en: "ClawEase plugin" },
  "howItWorks.visual.learn.start": { zh: "开始探索", en: "Start exploring" },
  "howItWorks.visual.learn.command": { zh: "输入探索指令", en: "Enter exploration command" },
  "howItWorks.visual.learn.rule1": { zh: "识别表单", en: "Form detected" },
  "howItWorks.visual.learn.rule2": { zh: "识别提交", en: "Submit detected" },
  "howItWorks.visual.learn.rule3": { zh: "生成工作流", en: "Workflow generated" },
  "howItWorks.visual.learn.confirm": { zh: "确认工作流", en: "Confirm workflow" },
  "howItWorks.visual.learn.done": { zh: "已完成", en: "Completed" },
  "howItWorks.visual.catch.hub": { zh: "实时接入中枢", en: "Live intake hub" },
  "howItWorks.visual.catch.coverage": { zh: "Phone 优先，全渠道接入", en: "Calls first. Every channel connected." },
  "howItWorks.visual.catch.live": { zh: "实时在线", en: "Live" },
  "howItWorks.visual.catch.call": { zh: "来电", en: "Incoming call" },
  "howItWorks.visual.catch.answered": { zh: "3 秒内响应", en: "Answered in 3s" },
  "howItWorks.visual.catch.caller": { zh: "客户", en: "Caller" },
  "howItWorks.visual.catch.callerTime": { zh: "客户：pm 10:45:01", en: "Caller: pm 10:45:01" },
  "howItWorks.visual.catch.replyTime": { zh: "ClawEase：pm 10:45:03", en: "ClawEase: pm 10:45:03" },
  "howItWorks.visual.catch.callText": { zh: "我想预约下周做个皮肤美容。", en: "I'd like to book a skin treatment next week." },
  "howItWorks.visual.catch.replyText": { zh: "当然可以，我在线为您登记。", en: "Of course. I can help you get that started." },
  "howItWorks.visual.catch.captured": { zh: "已识别预约请求", en: "Booking request captured" },
  "howItWorks.visual.catch.listening": { zh: "ClawEase 正在倾听", en: "ClawEase is listening" },
  "howItWorks.visual.catch.channelOnline": { zh: "全渠道在线", en: "All channels online" },
  "howItWorks.visual.catch.lineTitle": { zh: "LINE", en: "LINE" },
  "howItWorks.visual.catch.lineDesc": { zh: "改期咨询", en: "Reschedule inquiry" },
  "howItWorks.visual.catch.whatsappTitle": { zh: "WhatsApp", en: "WhatsApp" },
  "howItWorks.visual.catch.whatsappDesc": { zh: "价格咨询", en: "Pricing question" },
  "howItWorks.visual.catch.emailTitle": { zh: "Email", en: "Email" },
  "howItWorks.visual.catch.emailDesc": { zh: "新客户咨询", en: "New customer inquiry" },
  "howItWorks.visual.catch.formsTitle": { zh: "Forms", en: "Forms" },
  "howItWorks.visual.catch.formsDesc": { zh: "信息已捕获", en: "Details captured" },
  "howItWorks.visual.catch.smsTitle": { zh: "SMS", en: "SMS" },
  "howItWorks.visual.catch.smsDesc": { zh: "确认时间偏好", en: "Time preference confirmed" },
  "howItWorks.visual.catch.missed": { zh: "漏接", en: "missed" },
  "howItWorks.visual.catch.channels": { zh: "渠道", en: "channels" },
  "howItWorks.visual.catch.online": { zh: "在线", en: "online" },
  "howItWorks.visual.understand.live": { zh: "实时对话", en: "Live conversation" },
  "howItWorks.visual.understand.extracting": { zh: "扫描提取中", en: "Extracting" },
  "howItWorks.visual.understand.structured": { zh: "结构化提取", en: "Structured extraction" },
  "howItWorks.visual.understand.customer": { zh: "客户", en: "Customer" },
  "howItWorks.visual.understand.clawease": { zh: "ClawEase", en: "ClawEase" },
  "howItWorks.visual.understand.user": { zh: "我想约下周五找李医生做个皮肤管理。", en: "I'd like to book next Friday with Dr. Lee for skin care." },
  "howItWorks.visual.understand.agent": { zh: "好的，请问您的手机号是？", en: "Sure. What's your phone number?" },
  "howItWorks.visual.understand.userPrefix": { zh: "我想约", en: "I'd like to book" },
  "howItWorks.visual.understand.doctorPrefix": { zh: "找", en: "with" },
  "howItWorks.visual.understand.servicePrefix": { zh: "做个", en: "for" },
  "howItWorks.visual.understand.agentAsk": { zh: "好的，请问您的手机号是？", en: "Sure. What's your phone number?" },
  "howItWorks.visual.understand.agentConfirm": { zh: "收到，我会补全预约信息。", en: "Got it. I'll complete the booking details." },
  "howItWorks.visual.understand.intent": { zh: "识别意图", en: "Intent detected" },
  "howItWorks.visual.understand.booking": { zh: "预约", en: "Booking" },
  "howItWorks.visual.understand.fields": { zh: "字段", en: "Fields" },
  "howItWorks.visual.understand.fieldTime": { zh: "时间", en: "Time" },
  "howItWorks.visual.understand.fieldDoctor": { zh: "医生", en: "Provider" },
  "howItWorks.visual.understand.fieldService": { zh: "项目", en: "Service" },
  "howItWorks.visual.understand.fieldPhone": { zh: "手机", en: "Phone" },
  "howItWorks.visual.understand.timeValue": { zh: "下周五", en: "next Friday" },
  "howItWorks.visual.understand.doctorValue": { zh: "李医生", en: "Dr. Lee" },
  "howItWorks.visual.understand.serviceValue": { zh: "皮肤管理", en: "skin care" },
  "howItWorks.visual.understand.phoneValue": { zh: "13800001234", en: "13800001234" },
  "howItWorks.visual.understand.waiting": { zh: "等待中...", en: "Waiting..." },
  "howItWorks.visual.execute.form": { zh: "预约表单", en: "Booking form" },
  "howItWorks.visual.execute.title": { zh: "新预约", en: "New appointment" },
  "howItWorks.visual.execute.auto": { zh: "自动填写中", en: "Auto filling" },
  "howItWorks.visual.execute.chrome": { zh: "Chrome", en: "Chrome" },
  "howItWorks.visual.execute.control": { zh: "自动操作网页", en: "Automates the web" },
  "howItWorks.visual.execute.controlSub": { zh: "打开 · 填写 · 提交", en: "Open · Fill · Submit" },
  "howItWorks.visual.execute.url": { zh: "app.clinic.com/booking", en: "app.clinic.com/booking" },
  "howItWorks.visual.execute.loading": { zh: "页面加载中...", en: "Loading page..." },
  "howItWorks.visual.execute.customer": { zh: "客户", en: "Customer" },
  "howItWorks.visual.execute.customerValue": { zh: "Mia Chen", en: "Mia Chen" },
  "howItWorks.visual.execute.phone": { zh: "手机", en: "Phone" },
  "howItWorks.visual.execute.phoneValue": { zh: "13800001234", en: "13800001234" },
  "howItWorks.visual.execute.service": { zh: "服务", en: "Service" },
  "howItWorks.visual.execute.serviceValue": { zh: "皮肤管理", en: "Skin care" },
  "howItWorks.visual.execute.time": { zh: "时间", en: "Time" },
  "howItWorks.visual.execute.timeValue": { zh: "下周五 14:00", en: "Next Fri 2:00 PM" },
  "howItWorks.visual.execute.submit": { zh: "提交预约", en: "Submit Booking" },
  "howItWorks.visual.execute.submitting": { zh: "提交中...", en: "Submitting..." },
  "howItWorks.visual.execute.ready": { zh: "就绪", en: "Ready" },
  "howItWorks.visual.execute.successTitle": { zh: "预约成功！", en: "Booking Confirmed!" },
  "howItWorks.visual.execute.successLine1": { zh: "Mia Chen · 下周五 14:00", en: "Mia Chen · Next Fri 2:00 PM" },
  "howItWorks.visual.execute.successLine2": { zh: "皮肤管理 · 已提交", en: "Skin care · Submitted" },
  "howItWorks.visual.execute.completedBy": { zh: "ClawEase 自动完成", en: "Completed by ClawEase" },
  "howItWorks.visual.report.confirmed": { zh: "预约已确认", en: "Booking confirmed" },
  "howItWorks.visual.report.confirmedDesc": { zh: "周五 10:30。确认信息已发送给客户。", en: "Friday, 10:30 AM. Confirmation sent to customer." },
  "howItWorks.visual.report.summary": { zh: "团队摘要", en: "Team summary" },
  "howItWorks.visual.report.intent": { zh: "意图：预约", en: "Intent: Booking" },
  "howItWorks.visual.report.service": { zh: "服务：初次咨询", en: "Service: Initial consultation" },
  "howItWorks.visual.report.status": { zh: "状态：已提交", en: "Status: Submitted" },
  "howItWorks.visual.report.log1": { zh: "已登录预约门户", en: "Logged into booking portal" },
  "howItWorks.visual.report.log2": { zh: "已完成必填字段", en: "Completed required fields" },
  "howItWorks.visual.report.log3": { zh: "已提交请求并发送摘要", en: "Submitted request and sent summary" },
  "howItWorks.visual.report.dashboard.nav.home": { zh: "首页", en: "Home" },
  "howItWorks.visual.report.dashboard.nav.workflow": { zh: "工作流", en: "Workflows" },
  "howItWorks.visual.report.dashboard.nav.records": { zh: "执行记录", en: "Execution Records" },
  "howItWorks.visual.report.dashboard.nav.channels": { zh: "渠道", en: "Channels" },
  "howItWorks.visual.report.dashboard.owner": { zh: "张总", en: "Zhang" },
  "howItWorks.visual.report.sidebar.overview": { zh: "概览", en: "Overview" },
  "howItWorks.visual.report.sidebar.workflow": { zh: "流程", en: "Flow" },
  "howItWorks.visual.report.sidebar.records": { zh: "记录", en: "Records" },
  "howItWorks.visual.report.sidebar.channels": { zh: "渠道", en: "Channels" },
  "howItWorks.visual.report.sidebar.team": { zh: "团队", en: "Team" },
  "howItWorks.visual.report.sidebar.settings": { zh: "设置", en: "Settings" },
  "howItWorks.visual.report.dashboard.title": { zh: "今日反馈概览", en: "Today's feedback overview" },
  "howItWorks.visual.report.dashboard.subtitle": { zh: "自动捕获、填写、提交的执行结果", en: "Execution results from auto-capture, fill, and submit" },
  "howItWorks.visual.report.dashboard.today": { zh: "今天", en: "Today" },
  "howItWorks.visual.report.dashboard.stat.today": { zh: "今日预约", en: "Bookings today" },
  "howItWorks.visual.report.dashboard.stat.complete": { zh: "完成率", en: "Completion rate" },
  "howItWorks.visual.report.dashboard.stat.missed": { zh: "漏接", en: "Missed" },
  "howItWorks.visual.report.dashboard.stat.avgTime": { zh: "平均耗时", en: "Avg. time" },
  "howItWorks.visual.report.record.title": { zh: "执行记录", en: "Execution records" },
  "howItWorks.visual.report.record.filter": { zh: "今天", en: "Today" },
  "howItWorks.visual.report.record.time": { zh: "时间", en: "Time" },
  "howItWorks.visual.report.record.customer": { zh: "客户", en: "Customer" },
  "howItWorks.visual.report.record.business": { zh: "业务", en: "Request" },
  "howItWorks.visual.report.record.channel": { zh: "渠道", en: "Channel" },
  "howItWorks.visual.report.record.status": { zh: "状态", en: "Status" },
  "howItWorks.visual.report.record.duration": { zh: "耗时", en: "Time" },
  "howItWorks.visual.report.record.customer1": { zh: "王女士", en: "Ms. Wang" },
  "howItWorks.visual.report.record.business1": { zh: "水光针", en: "Hydrafacial" },
  "howItWorks.visual.report.record.customer2": { zh: "李先生", en: "Mr. Li" },
  "howItWorks.visual.report.record.business2": { zh: "改期", en: "Reschedule" },
  "howItWorks.visual.report.record.customer3": { zh: "陈女士", en: "Ms. Chen" },
  "howItWorks.visual.report.record.business3": { zh: "咨询", en: "Inquiry" },
  "howItWorks.visual.report.record.customer4": { zh: "周先生", en: "Mr. Zhou" },
  "howItWorks.visual.report.record.business4": { zh: "预约", en: "Booking" },
  "howItWorks.visual.report.record.phone": { zh: "电话", en: "Phone" },
  "howItWorks.visual.report.record.form": { zh: "表单", en: "Form" },
  "howItWorks.visual.report.record.statusSubmitted": { zh: "已提交", en: "Submitted" },
  "howItWorks.visual.report.record.statusConfirmed": { zh: "已确认", en: "Confirmed" },
  "howItWorks.visual.report.record.statusReplied": { zh: "已回复", en: "Replied" },
  "howItWorks.visual.report.dag.title": { zh: "任务依赖 DAG", en: "Task Dependency DAG" },
  "howItWorks.visual.report.dag.subtitle": {
    zh: "按任务关系生成，点击节点查看执行结果和会话过程。",
    en: "Generated by task relationships. Open nodes to inspect results and sessions.",
  },
  "howItWorks.visual.report.dag.count": { zh: "31 个任务", en: "31 tasks" },
  "howItWorks.visual.report.dag.result": { zh: "结果", en: "results" },
  "howItWorks.visual.report.phone.owner": { zh: "张店长的 iPhone", en: "Manager Zhang's iPhone" },
  "howItWorks.visual.report.phone.app": { zh: "短信 · 现在", en: "Messages · Now" },
  "howItWorks.visual.report.phone.now": { zh: "刚刚", en: "Now" },
  "howItWorks.visual.report.phone.bot": { zh: "ClawEase", en: "ClawEase" },
  "howItWorks.visual.report.phone.title": { zh: "王女士预约已确认", en: "Ms. Wang's booking is confirmed" },
  "howItWorks.visual.report.phone.subtitle": { zh: "水光针 · 周六 14:00", en: "Hydrafacial · Sat 2:00 PM" },
  "howItWorks.visual.report.phone.status": { zh: "已写入预约系统", en: "Saved to booking system" },
  "howItWorks.visual.report.chat.group": { zh: "浦东店运营群 (12)", en: "Pudong Ops Group (12)" },
  "howItWorks.visual.report.chat.tab1": { zh: "钉钉", en: "DingTalk" },
  "howItWorks.visual.report.chat.tab2": { zh: "企微", en: "WeCom" },
  "howItWorks.visual.report.chat.tab3": { zh: "飞书", en: "Lark" },
  "howItWorks.visual.report.chat.botTitle": { zh: "执行成功：王女士预约已确认", en: "Done: Ms. Wang's booking is confirmed" },
  "howItWorks.visual.report.chat.botLine1": { zh: "水光针 · 本周六 14:00", en: "Hydrafacial · This Saturday 2:00 PM" },
  "howItWorks.visual.report.chat.botLine2": { zh: "来源 WhatsApp · 耗时 1分24秒", en: "Source: WhatsApp · 1m24s" },
  "howItWorks.visual.report.chat.botLine3": { zh: "已写入预约系统", en: "Saved to booking system" },
  "howItWorks.visual.report.chat.manager": { zh: "张店长", en: "Manager Zhang" },
  "howItWorks.visual.report.chat.managerReply": { zh: "收到，小李提前准备下房间和物料。", en: "Got it. Li, please prep the room and supplies." },
  "howItWorks.visual.report.chat.staff": { zh: "小李", en: "Li" },
  "howItWorks.visual.report.chat.staffReply": { zh: "好的店长，我现在处理。", en: "Sure, manager. I'm on it now." },
  "howItWorks.visual.report.chat.input": { zh: "输入消息...", en: "Type a message..." },
  "howItWorks.visual.report.chat.synced": { zh: "已同步到工作群消息记录", en: "Synced to group message log" },

  // Three Act Flow (Jafar-style)
  "acts.label": { zh: "三段式流程", en: "Three-Act Flow" },
  "acts.title": { zh: "从电话到确认预约——一口气完成", en: "From call to confirmed appointment — in one breath" },
  "acts.1.title": { zh: "它接听", en: "It listens" },
  "acts.1.desc": { zh: "每通电话 3 秒内应答，支持任意语言，像资深前台一样从容。", en: "Every call answered in three seconds, in any language, with the calm of a senior receptionist." },
  "acts.2.title": { zh: "它理解", en: "It understands" },
  "acts.2.desc": { zh: "意图、紧急程度、客户历史——对话过程中实时结构化到你的系统。", en: "Intent, urgency, history — structured into your tools as the conversation unfolds." },
  "acts.3.title": { zh: "它执行", en: "It books" },
  "acts.3.desc": { zh: "预约确认进入你的日历，确认信息进入客户手机。悄然无声，每次都一样。", en: "A confirmed appointment in your calendar. A confirmation in your customer's pocket. Quietly, every time." },

  // Capabilities
  "cap.label": { zh: "核心能力", en: "Core Capabilities" },
  "cap.title": { zh: "组合能力，不是单点 AI 对话", en: "Combined capabilities, not just AI chat" },
  "cap.multi": { zh: "多输入端接入", en: "Multi-Channel Input" },
  "cap.multi.desc": { zh: "电话、LINE、WhatsApp、Email、表单——每个市场、每个渠道统一接入", en: "Phone, LINE, WhatsApp, Email, forms — every market, every channel, unified" },
  "cap.ai": { zh: "AI 接管 + 意图识别", en: "AI Takeover + Intent" },
  "cap.ai.desc": { zh: "自动接听/回复，判断客户想咨询、预约、改期还是查询", en: "Auto answer/reply, determine inquiry, booking, reschedule, or status check" },
  "cap.data": { zh: "结构化信息采集", en: "Structured Data Capture" },
  "cap.data.desc": { zh: "姓名、电话、服务项目、时间偏好——按你的流程逐字段采集", en: "Name, phone, service type, time preferences — field by field, per your workflow" },
  "cap.browser": { zh: "浏览器自动化", en: "Browser Automation" },
  "cap.browser.desc": { zh: "像员工一样操作现有网页系统，无需 API 对接", en: "Operate existing web systems like a human — no API integration needed" },
  "cap.execute": { zh: "业务流程执行", en: "Workflow Execution" },
  "cap.execute.desc": { zh: "创建预约、填写表单、提交请求、更新记录——完整回路", en: "Create bookings, fill forms, submit requests, update records — the full loop" },
  "cap.handoff": { zh: "人工转接 + 可控执行", en: "Human Handoff + Control" },
  "cap.handoff.desc": { zh: "复杂问题转人工；可设确认、边界、审计日志", en: "Escalate complex cases; configurable confirmations, boundaries, audit logs" },
  "cap.notify": { zh: "结果摘要通知", en: "Summary & Notification" },
  "cap.notify.desc": { zh: "客户需求 + 采集信息 + 执行结果直接发送给团队", en: "Send captured data and execution results directly to your team" },
  "cap.cross": { zh: "跨行业复用", en: "Cross-Industry Ready" },
  "cap.cross.desc": { zh: "医美、牙科、宠物、教育、维修、酒店——均可适配", en: "Aesthetics, dental, pet services, education, repair, hospitality — all adaptable" },
  "cap.zero": { zh: "零 API 对接", en: "Zero API" },
  "cap.zero.desc": { zh: "直接操作你现有的网页系统，无需任何开发工作", en: "Operate your existing web systems directly — no dev work required" },

  // Boundaries
  "bound.label": { zh: "产品边界", en: "Product Boundaries" },
  "bound.title": { zh: "做什么，不做什么", en: "What it does and doesn't do" },
  "bound.do.title": { zh: "ClawEase 做什么", en: "What ClawEase Does" },
  "bound.do.1": { zh: "捕获全渠道客户请求", en: "Capture customer requests across all channels" },
  "bound.do.2": { zh: "理解意图并采集结构化信息", en: "Understand intent and collect structured info" },
  "bound.do.3": { zh: "通过浏览器执行标准网页流程", en: "Execute standard web workflows via browser" },
  "bound.do.4": { zh: "创建预约、提交表单、更新记录", en: "Create bookings, submit forms, update records" },
  "bound.do.5": { zh: "向团队发送摘要通知", en: "Notify human teams with summaries" },
  "bound.do.6": { zh: "带完整上下文转接复杂问题", en: "Escalate complex cases with full context" },
  "bound.dont.title": { zh: "ClawEase 不是什么", en: "What ClawEase Is Not" },
  "bound.dont.1": { zh: "不是普通 AI 电话机器人", en: "Not a generic AI phone bot" },
  "bound.dont.2": { zh: "不是简单 chatbot", en: "Not a simple chatbot" },
  "bound.dont.3": { zh: "不是传统客服系统", en: "Not a traditional customer service system" },
  "bound.dont.4": { zh: "不是新的预约系统或 CRM 替代品", en: "Not a new booking system or CRM replacement" },
  "bound.dont.5": { zh: "不是医疗/法律/财务专业判断工具", en: "Not a medical/legal/financial judgment tool" },

  // Comparison
  "comp.label": { zh: "竞品差异", en: "Competitive Edge" },
  "comp.title": { zh: "不是又一个 AI 电话机器人", en: "Not another AI phone bot" },
  "comp.old.col": { zh: "普通 AI 电话 / AI 前台", en: "Generic AI Phone / Receptionist" },
  "comp.new.col": { zh: "ClawEase", en: "ClawEase" },
  "comp.old.1": { zh: "接电话", en: "Answers calls" },
  "comp.new.1": { zh: "接住多种输入端", en: "Handles multiple input channels" },
  "comp.old.2": { zh: "回答问题", en: "Answers questions" },
  "comp.new.2": { zh: "理解业务意图", en: "Understands business intent" },
  "comp.old.3": { zh: "做摘要", en: "Produces summaries" },
  "comp.new.3": { zh: "采集结构化字段", en: "Captures structured fields" },
  "comp.old.4": { zh: "发通知", en: "Sends notifications" },
  "comp.new.4": { zh: "自动执行网页操作", en: "Executes web operations automatically" },
  "comp.old.5": { zh: "转人工", en: "Transfers to human" },
  "comp.new.5": { zh: "完成或半完成业务流程", en: "Completes or semi-completes workflows" },
  "comp.old.6": { zh: "依赖 API 集成", en: "Requires API integration" },
  "comp.new.6": { zh: "可操作现有网页系统，无需 API", en: "Operates existing web systems — no API" },
  "comp.old.7": { zh: "偏客服", en: "Leans customer service" },
  "comp.new.7": { zh: "偏业务执行", en: "Leans business execution" },

  // Capabilities
  "useCases.eyebrow": { zh: "核心能力", en: "CAPABILITIES" },
  "useCases.title": {
    zh: "把最容易流失的客户请求，化为自动执行的业务闭环",
    en: "Turn the easiest-to-lose customer requests into automated business loops",
  },
  "useCases.subtitle": {
    zh: "针对高价值客户咨询场景打造，不仅在最核心的短咨询与预约中实现秒级落地，更全面覆盖改期、补录、跨系统分派等低频耗时动作，无需改造系统，让增长不再流失。",
    en: "Built for high-value customer inquiry scenarios, ClawEase handles core short consultations and bookings in seconds, then extends to reschedules, data backfills, cross-system dispatches, and other low-frequency but time-consuming operations without changing your existing systems, so growth no longer slips away.",
  },
  "useCases.tier1.label": { zh: "TIER 1 · 高价值核心能力", en: "TIER 1 · HIGH-VALUE CORE CAPABILITIES" },
  "useCases.tier1.title": { zh: "优先接住最影响转化的客户请求", en: "Start with the requests that most directly affect conversion" },
  "useCases.tier1.sub": { zh: "把最影响转化的核心线索，变成系统里的确定预约。", en: "Turn the highest-converting leads into confirmed bookings inside your system." },
  "useCases.fit.title": { zh: "场景特征", en: "Scenario signals" },
  "useCases.fit.sub": {
    zh: "高价值请求通常有这些共同点，越符合越容易看见自动化收益。",
    en: "High-value request workflows tend to share these traits, making automation value easier to see.",
  },
  "useCases.fit.1.title": { zh: "客户请求有商业价值", en: "Requests carry commercial value" },
  "useCases.fit.1.desc": { zh: "一次咨询或预约，可能直接影响成交、到店或复购。", en: "One inquiry or booking can affect conversion, visits, or repeat business." },
  "useCases.fit.2.title": { zh: "入口分散且容易漏接", en: "Inputs are fragmented" },
  "useCases.fit.2.desc": { zh: "电话、消息、表单同时存在，人工很难稳定接住。", en: "Calls, messages, and forms arrive together, making consistent handling hard." },
  "useCases.fit.3.title": { zh: "后续动作相对标准", en: "Follow-up actions are repeatable" },
  "useCases.fit.3.desc": { zh: "查信息、问字段、填系统、提交结果，有明确规则。", en: "Checking, asking, filling, and submitting follow clear rules." },
  "useCases.fit.4.title": { zh: "人工重复但不该被忽视", en: "Manual work is repetitive but important" },
  "useCases.fit.4.desc": { zh: "不复杂，却经常占用前台、顾问或运营团队的注意力。", en: "Not complex, but it drains front desk, consultant, or ops attention." },
  "useCases.tier1.unified.lead": { zh: "统一特征：", en: "Common pattern: " },
  "useCases.tier1.unified.body": {
    zh: "客户通过电话或消息提出需求，团队需要进入网页系统完成后续操作。ClawEase 接住请求，并把动作推进到可交付结果。",
    en: "A customer asks through phone or messaging, and the team needs to operate a web system afterward. ClawEase captures the request and advances it toward a deliverable result.",
  },
  "useCases.tier2.label": { zh: "TIER 2 · 日常运营场景", en: "TIER 2 · DAILY OPERATIONS" },
  "useCases.tier2.title": { zh: "低频但耗时的运营动作，也能标准化接住", en: "Low-frequency but time-consuming operations can be standardized too" },
  "useCases.tier2.sub": {
    zh: "不一定直接带来成交，却持续占用团队注意力。ClawEase 适合承接改期、补录、分派、提醒等规则明确、重复发生的流程。",
    en: "They may not directly create conversion, but they constantly consume team attention. ClawEase is suited for repeatable, rule-based flows like rescheduling, data backfill, dispatch, and reminders.",
  },
  "useCases.tier3.label": { zh: "已支持行业", en: "Supported industries" },
  "useCases.tier3.title": { zh: "已支持医美、牙科和宠物服务", en: "Supported today: aesthetics, dental, and pet services" },
  "useCases.tier3.sub": {
    zh: "同一个智能执行引擎，面向不同高客单、重预约服务团队生成不同业务结果。",
    en: "Same intelligent engine, tuned for different high-ticket, booking-heavy service outcomes.",
  },
  "useCases.tier3.expand": { zh: "可扩展行业", en: "Expandable industries" },
  "useCases.painTitle": { zh: "ClawEase 解决", en: "ClawEase solves" },
  "useCases.chainTitle": { zh: "四段动作链", en: "FOUR-STEP ACTION CHAIN" },
  "useCases.chainSub": { zh: "从客户输入到系统动作，一条链跑完", en: "From customer input to system action in one operational chain" },
  "useCases.chainBadge": { zh: "无需 API，操作现有网页系统", en: "No API, existing web systems" },
  "useCases.action.1": { zh: "渠道输入", en: "Channel input" },
  "useCases.action.2": { zh: "AI 理解", en: "AI understanding" },
  "useCases.action.3": { zh: "系统操作", en: "System operation" },
  "useCases.action.4": { zh: "业务反馈", en: "Business feedback" },
  "useCases.longtail.actionLabel": { zh: "AI 动作", en: "AI ACTION" },

  "useCases.consultation.title": { zh: "短咨询", en: "Short inquiries" },
  "useCases.consultation.desc": {
    zh: "服务咨询、价格咨询、资格判断、营业时间等高频问题，快速理解并稳定回复，把真正有价值的请求筛出来。",
    en: "Handle service questions, pricing, eligibility checks, hours, and other frequent inquiries while surfacing the requests worth follow-up.",
  },
  "useCases.consultation.example.1": { zh: "项目 / 服务咨询", en: "Service questions" },
  "useCases.consultation.example.2": { zh: "价格 / 可用性", en: "Price / availability" },
  "useCases.consultation.example.3": { zh: "资格 / 材料确认", en: "Eligibility / requirements" },
  "useCases.consultation.metric.1.value": { zh: "24/7", en: "24/7" },
  "useCases.consultation.metric.1.label": { zh: "全天候响应", en: "Always-on response" },
  "useCases.consultation.metric.2.value": { zh: "0%", en: "0%" },
  "useCases.consultation.metric.2.label": { zh: "高价值请求漏接率", en: "High-value request miss rate" },

  "useCases.appointment.title": { zh: "预约", en: "Bookings" },
  "useCases.appointment.desc": {
    zh: "客户已经表达明确意向后，ClawEase 继续完成时间确认、信息采集、系统登记和结果同步。",
    en: "After intent is clear, ClawEase continues with time confirmation, data capture, system entry, and result syncing.",
  },
  "useCases.appointment.example.1": { zh: "新客预约", en: "New booking" },
  "useCases.appointment.example.2": { zh: "改期 / 取消", en: "Reschedule / cancel" },
  "useCases.appointment.example.3": { zh: "到店 / 上门安排", en: "Visit / onsite scheduling" },
  "useCases.appointment.metric.1.value": { zh: "+23%", en: "+23%" },
  "useCases.appointment.metric.1.label": { zh: "预约转化提升", en: "Booking lift" },
  "useCases.appointment.metric.2.value": { zh: ">99%", en: ">99%" },
  "useCases.appointment.metric.2.label": { zh: "标准化执行完成率", en: "Standard execution rate" },

  "useCases.booking.tab": { zh: "诊所 / 服务预约", en: "Clinics & bookings" },
  "useCases.booking.title": { zh: "诊所与本地服务预约", en: "Clinics and local-service bookings" },
  "useCases.booking.positioning": {
    zh: "从多渠道接入客户咨询，自动理解项目、时间和偏好，再进入预约系统锁定时段。",
    en: "Capture inquiries across channels, understand service, time, and preference, then lock the slot in the booking system.",
  },
  "useCases.booking.industries": { zh: "适用于医美、牙科、宠物医院、健身工作室、教培和美容 SPA。", en: "For aesthetics, dental, pet clinics, fitness studios, education, and beauty services." },
  "useCases.booking.pain.1": { zh: "客户下班后咨询，次日才回复，预约机会被竞争对手接走。", en: "After-hours inquiries wait until the next day and often go to competitors." },
  "useCases.booking.pain.2": { zh: "项目、价格、时间、医生偏好都要问清楚，人工答复不稳定。", en: "Service, price, time, and provider preference need consistent collection." },
  "useCases.booking.pain.3": { zh: "锁定预约要登录系统、查排班、填表、提交，人工重复耗时。", en: "Booking requires login, schedule lookup, form filling, and submission." },
  "useCases.booking.metric.1.value": { zh: "8 秒", en: "8s" },
  "useCases.booking.metric.1.label": { zh: "新客首次响应", en: "First response" },
  "useCases.booking.metric.2.value": { zh: "+23%", en: "+23%" },
  "useCases.booking.metric.2.label": { zh: "预约转化提升", en: "Booking lift" },
  "useCases.booking.step.1": { zh: "电话、WhatsApp、LINE、表单进入：我想预约下周做个皮肤美容。", en: "Phone, WhatsApp, LINE, or form: I want to book a skin treatment next week." },
  "useCases.booking.step.2": { zh: "识别意图=预约，提取项目、日期、时间偏好和客户身份。", en: "Detect booking intent and extract service, date, time preference, and customer identity." },
  "useCases.booking.step.3": { zh: "打开预约系统，查询可用时段，填写客户资料并提交预约。", en: "Open the booking system, check availability, fill customer details, and submit." },
  "useCases.booking.step.4": { zh: "同步客户确认信息，并向运营团队推送执行摘要。", en: "Send customer confirmation and push an execution summary to the team." },
  "useCases.booking.tag.1": { zh: "电话", en: "Phone" },
  "useCases.booking.tag.2": { zh: "LINE", en: "LINE" },
  "useCases.booking.tag.3": { zh: "表单", en: "Forms" },
  "useCases.booking.tag.4": { zh: "意图识别", en: "Intent" },
  "useCases.booking.tag.5": { zh: "时间提取", en: "Time" },
  "useCases.booking.tag.6": { zh: "偏好采集", en: "Preference" },
  "useCases.booking.tag.7": { zh: "预约系统", en: "Booking system" },
  "useCases.booking.tag.8": { zh: "网页执行", en: "Web execution" },
  "useCases.booking.tag.9": { zh: "客户确认", en: "Customer confirmation" },
  "useCases.booking.tag.10": { zh: "团队摘要", en: "Team summary" },
  "useCases.booking.quote": { zh: "以前咨询师下班就断线，现在深夜请求也能被接住，第二天上班已经看到待确认预约。", en: "Before, inquiries stopped after staff hours. Now after-hours requests are captured and bookings are ready for review in the morning." },
  "useCases.booking.author": { zh: "本地服务业运营负责人", en: "Operations lead, local service business" },

  "useCases.ecommerce.tab": { zh: "高价值跨境订单", en: "High-value commerce" },
  "useCases.ecommerce.title": { zh: "高价值跨境电商订单", en: "High-value cross-border commerce" },
  "useCases.ecommerce.positioning": {
    zh: "面向海外客户的大额询价，AI 自动识别 SKU、数量、目的地和客户等级，再进入 ERP 查询库存与报价。",
    en: "For high-value global inquiries, AI identifies SKU, quantity, destination, and customer tier, then checks inventory and pricing in ERP.",
  },
  "useCases.ecommerce.industries": { zh: "适用于 B2B 贸易、高客单 DTC、海外分销和跨境工厂。", en: "For B2B trade, high-ticket DTC, overseas distribution, and cross-border factories." },
  "useCases.ecommerce.pain.1": { zh: "客户跨时区咨询，国内团队经常错过海外黄金回复时间。", en: "Global buyers ask across time zones and teams miss prime response windows." },
  "useCases.ecommerce.pain.2": { zh: "大额询价要查 SKU、库存、物流和价格策略，人工流程长。", en: "Large inquiries require SKU, inventory, shipping, and pricing checks." },
  "useCases.ecommerce.pain.3": { zh: "WhatsApp、Email、表单分散，线索容易丢在不同入口。", en: "WhatsApp, email, and forms are scattered, causing leads to slip." },
  "useCases.ecommerce.metric.1.value": { zh: "11 秒", en: "11s" },
  "useCases.ecommerce.metric.1.label": { zh: "大额询单响应", en: "Large inquiry response" },
  "useCases.ecommerce.metric.2.value": { zh: "+18%", en: "+18%" },
  "useCases.ecommerce.metric.2.label": { zh: "订单转化提升", en: "Order conversion lift" },
  "useCases.ecommerce.step.1": { zh: "WhatsApp / Email 进入：需要 500 件 SKU-A88，什么时候能发货？", en: "WhatsApp or email: Need 500 units of SKU-A88. When can it ship?" },
  "useCases.ecommerce.step.2": { zh: "识别 SKU、数量、目的地、客户等级和询价紧迫度。", en: "Extract SKU, quantity, destination, customer tier, and urgency." },
  "useCases.ecommerce.step.3": { zh: "登录 ERP 查询库存、价格规则和发货时效，生成可回复方案。", en: "Log into ERP to check inventory, pricing rules, shipping timing, and draft a response." },
  "useCases.ecommerce.step.4": { zh: "向客户回复可执行报价，并将商机同步给销售团队。", en: "Reply with an actionable quote and sync the opportunity to sales." },
  "useCases.ecommerce.tag.1": { zh: "WhatsApp", en: "WhatsApp" },
  "useCases.ecommerce.tag.2": { zh: "Email", en: "Email" },
  "useCases.ecommerce.tag.3": { zh: "表单", en: "Forms" },
  "useCases.ecommerce.tag.4": { zh: "SKU 解析", en: "SKU parsing" },
  "useCases.ecommerce.tag.5": { zh: "客户分层", en: "Customer tier" },
  "useCases.ecommerce.tag.6": { zh: "多语言", en: "Multilingual" },
  "useCases.ecommerce.tag.7": { zh: "ERP", en: "ERP" },
  "useCases.ecommerce.tag.8": { zh: "库存报价", en: "Inventory & quote" },
  "useCases.ecommerce.tag.9": { zh: "客户回复", en: "Buyer reply" },
  "useCases.ecommerce.tag.10": { zh: "销售同步", en: "Sales sync" },
  "useCases.ecommerce.quote": { zh: "客户在海外白天发来的大额询价，不再等到我们第二天上班才处理。", en: "High-value inquiries from global buyers no longer wait for our next business day." },
  "useCases.ecommerce.author": { zh: "跨境贸易团队负责人", en: "Lead, cross-border sales team" },

  "useCases.longtail.reschedule.title": { zh: "改期 / 取消", en: "Reschedule & cancel" },
  "useCases.longtail.reschedule.desc": { zh: "客户临时改时间，自动查可用档期并更新记录。", en: "When customers change time, availability is checked and records are updated." },
  "useCases.longtail.reschedule.action": { zh: "释放原时段，查询新时段，重新提交预约。", en: "Release old slot, find a new slot, and resubmit the booking." },
  "useCases.longtail.reschedule.result": { zh: "客户收到新时间，团队同步变更记录。", en: "Customer gets the new time and the team sees the updated record." },
  "useCases.longtail.reschedule.metric": { zh: "9 秒完成", en: "9s avg" },
  "useCases.longtail.backfill.title": { zh: "数据补录", en: "Data backfill" },
  "useCases.longtail.backfill.desc": { zh: "字段缺失时主动追问，并将信息补齐到档案。", en: "When fields are missing, follow-up questions fill the record." },
  "useCases.longtail.backfill.action": { zh: "追问过敏史、地址、保险、偏好等缺失信息。", en: "Ask for missing allergy, address, insurance, or preference details." },
  "useCases.longtail.backfill.result": { zh: "资料完整后再进入执行流程。", en: "Once complete, the case moves into execution." },
  "useCases.longtail.backfill.metric": { zh: "字段补全", en: "Fields completed" },
  "useCases.longtail.dispatch.title": { zh: "工单分派", en: "Ticket dispatch" },
  "useCases.longtail.dispatch.desc": { zh: "客诉、维修、上门请求进入后，自动建单并分派责任人。", en: "Complaints, repairs, and onsite requests become assigned tickets." },
  "useCases.longtail.dispatch.action": { zh: "识别类型，匹配区域和人员，创建工单。", en: "Classify request, match region and owner, create a ticket." },
  "useCases.longtail.dispatch.result": { zh: "负责人收到完整上下文，客户收到处理确认。", en: "Owner gets context; customer gets handling confirmation." },
  "useCases.longtail.dispatch.metric": { zh: "自动分派", en: "Auto routed" },
  "useCases.longtail.followup.title": { zh: "回访 & 提醒", en: "Follow-up & reminders" },
  "useCases.longtail.followup.desc": { zh: "术后、复诊、订单跟进按规则自动触发。", en: "Post-care, revisit, and order follow-ups trigger by rule." },
  "useCases.longtail.followup.action": { zh: "按业务规则发送提醒，识别异常回复。", en: "Send reminders by rule and detect unusual replies." },
  "useCases.longtail.followup.result": { zh: "正常归档，异常转人工处理。", en: "Normal cases are archived; exceptions go to a human." },
  "useCases.longtail.followup.metric": { zh: "按时触达", en: "On-time" },

  "useCases.industry.core.1.title": { zh: "医美", en: "Medical Aesthetics" },
  "useCases.industry.core.1.desc": {
    zh: "把项目、价格和顾虑咨询，推进到高意向咨询预约。",
    en: "Turn treatment, pricing, and concern inquiries into qualified consultation bookings.",
  },
  "useCases.industry.core.2.title": { zh: "牙科诊所", en: "Dental Clinics" },
  "useCases.industry.core.2.desc": {
    zh: "把初诊、复诊和改期请求，推进到可确认预约与资料摘要。",
    en: "Turn new-patient and revisit requests into appointments and intake summaries.",
  },
  "useCases.industry.core.3.title": { zh: "宠物服务", en: "Pet Services" },
  "useCases.industry.core.3.desc": {
    zh: "把美容、寄养和疫苗请求，推进到服务预约与宠物档案。",
    en: "Turn grooming, boarding, and vaccine requests into bookings and pet profiles.",
  },
  "useCases.industry.seo.label": { zh: "行业 SEO 说明", en: "Industry SEO descriptions" },
  "useCases.industry.seo.title": {
    zh: "适用于医美、牙科和宠物服务的 AI 前台与 AI 接待",
    en: "AI front desk and AI receptionist for medical aesthetics, dental, and pet services",
  },
  "useCases.industry.seo.1.title": {
    zh: "医美 AI 前台与 AI 接待",
    en: "Medical spa AI front desk and AI receptionist",
  },
  "useCases.industry.seo.1.desc": {
    zh: "ClawEase 可作为医美诊所和 aesthetic clinic 的 AI front desk，承接电话、短信、WhatsApp、Email 和网页表单中的项目咨询、价格咨询、术前问题和 appointment booking 请求，并将高意向客户推进到咨询预约、资料采集和术前表单发送。",
    en: "ClawEase works as a medical spa AI front desk and AI receptionist for aesthetic clinics. It captures treatment questions, pricing inquiries, pre-care concerns, and appointment booking requests from phone, SMS, WhatsApp, email, and web forms, then moves high-intent leads into consultation booking, intake collection, and pre-care follow-up.",
  },
  "useCases.industry.seo.2.title": {
    zh: "牙科 AI 前台与 AI 接待",
    en: "Dental AI receptionist and AI front desk",
  },
  "useCases.industry.seo.2.desc": {
    zh: "ClawEase 可用于 dental office 和 dental clinic 的 AI receptionist 与 AI front desk，自动处理新客咨询、洗牙预约、复诊提醒、保险信息补全和 appointment scheduling，并把电话或消息中的患者偏好整理成可确认的预约和初诊资料。",
    en: "ClawEase supports dental offices and dental clinics as a dental AI receptionist and AI front desk. It handles new-patient inquiries, cleaning appointments, recall reminders, insurance follow-up, and appointment scheduling, turning phone or message conversations into ready-to-confirm bookings and intake summaries.",
  },
  "useCases.industry.seo.3.title": {
    zh: "宠物服务 AI 前台与 AI 接待",
    en: "Pet service AI receptionist and AI front desk",
  },
  "useCases.industry.seo.3.desc": {
    zh: "ClawEase 适用于 pet clinic、宠物美容、寄养和 boarding 服务的 AI receptionist 与 AI front desk，自动承接美容预约、疫苗问题、接送安排、宠物档案采集和服务后跟进，让电话、短信、WhatsApp、Email 与网页表单请求进入统一的 appointment booking 流程。",
    en: "ClawEase fits pet clinics, grooming, boarding, and pet service businesses as an AI receptionist and AI front desk. It captures grooming bookings, vaccination questions, pickup arrangements, pet profile details, and post-service follow-up across phone, SMS, WhatsApp, email, and web forms, then routes them into one appointment booking workflow.",
  },
  "useCases.industry.flow.request": { zh: "请求", en: "Request" },
  "useCases.industry.flow.message": { zh: "消息", en: "Message" },
  "useCases.industry.flow.phone": { zh: "电话", en: "Phone" },
  "useCases.industry.flow.operator": { zh: "ClawEase", en: "ClawEase" },
  "useCases.industry.flow.result": { zh: "结果", en: "Result" },
  "useCases.industry.core.1.result.title": { zh: "咨询已预约", en: "Consult booked" },
  "useCases.industry.core.1.result.scoreLabel": { zh: "线索评分", en: "Lead Score" },
  "useCases.industry.core.1.result.status": { zh: "高", en: "High" },
  "useCases.industry.core.1.result.person": { zh: "Alex Rivera", en: "Alex Rivera" },
  "useCases.industry.core.1.result.contact": { zh: "(555) 123-4567", en: "(555) 123-4567" },
  "useCases.industry.core.1.result.date": { zh: "5月20日", en: "May 20, 2025" },
  "useCases.industry.core.1.result.time": { zh: "2:30 PM", en: "2:30 PM" },
  "useCases.industry.core.1.result.owner": { zh: "顾问 Sarah", en: "Sarah, RN" },
  "useCases.industry.core.1.result.row.1.label": { zh: "项目", en: "Treatment" },
  "useCases.industry.core.1.result.row.1.value": { zh: "Botox 咨询", en: "Botox consult" },
  "useCases.industry.core.1.result.row.2.label": { zh: "顾虑", en: "Concern" },
  "useCases.industry.core.1.result.row.2.value": { zh: "抬头纹", en: "Frown lines" },
  "useCases.industry.core.1.result.row.3.label": { zh: "预算 / 意向", en: "Budget / intent" },
  "useCases.industry.core.1.result.row.3.value": { zh: "$600-$900", en: "$600-$900" },
  "useCases.industry.core.1.result.row.4.label": { zh: "术前表单", en: "Pre-care" },
  "useCases.industry.core.1.result.row.4.value": { zh: "已发送", en: "Sent" },
  "useCases.industry.core.2.result.title": { zh: "预约待确认", en: "Appointment ready" },
  "useCases.industry.core.2.result.status": { zh: "可确认", en: "Ready to Confirm" },
  "useCases.industry.core.2.result.date": { zh: "5月15日", en: "May 15, 2025" },
  "useCases.industry.core.2.result.person": { zh: "新客 · 洗牙", en: "New patient - cleaning" },
  "useCases.industry.core.2.result.time": { zh: "10:00-10:45 · Dr. Smith", en: "10:00-10:45 · Dr. Smith" },
  "useCases.industry.core.2.result.provider": { zh: "Dr. Smith", en: "Dr. Smith" },
  "useCases.industry.core.2.result.room": { zh: "诊室 2", en: "Operatory 2" },
  "useCases.industry.core.2.result.tag": { zh: "新客", en: "New" },
  "useCases.industry.core.2.result.row.1.label": { zh: "项目", en: "Service" },
  "useCases.industry.core.2.result.row.1.value": { zh: "洗牙", en: "Cleaning" },
  "useCases.industry.core.2.result.row.2.label": { zh: "医生 / 时间", en: "Provider / time" },
  "useCases.industry.core.2.result.row.2.value": { zh: "已确认", en: "Confirmed" },
  "useCases.industry.core.2.result.row.3.label": { zh: "保险", en: "Insurance" },
  "useCases.industry.core.2.result.row.3.value": { zh: "已补齐", en: "Complete" },
  "useCases.industry.core.2.result.row.4.label": { zh: "初诊资料", en: "Intake" },
  "useCases.industry.core.2.result.row.4.value": { zh: "已完成", en: "Completed" },
  "useCases.industry.core.3.result.title": { zh: "美容已预约", en: "Grooming booked" },
  "useCases.industry.core.3.result.status": { zh: "已确认", en: "Confirmed" },
  "useCases.industry.core.3.result.pet": { zh: "Buddy", en: "Buddy" },
  "useCases.industry.core.3.result.detail": { zh: "金毛 · 3岁 · 42 lbs", en: "Golden Retriever · 3 yrs · 42 lbs" },
  "useCases.industry.core.3.result.date": { zh: "5月18日", en: "May 18, 2025" },
  "useCases.industry.core.3.result.owner": { zh: "美容师 Jessica", en: "Groomer: Jessica" },
  "useCases.industry.core.3.result.row.1.label": { zh: "服务", en: "Service" },
  "useCases.industry.core.3.result.row.1.value": { zh: "全套美容", en: "Full groom" },
  "useCases.industry.core.3.result.row.2.label": { zh: "接送", en: "Pickup" },
  "useCases.industry.core.3.result.row.2.value": { zh: "12:00 PM", en: "12:00 PM" },
  "useCases.industry.core.3.result.row.3.label": { zh: "疫苗备注", en: "Vaccine note" },
  "useCases.industry.core.3.result.row.3.value": { zh: "已更新", en: "Updated" },
  "useCases.industry.core.3.result.row.4.label": { zh: "备注", en: "Notes" },
  "useCases.industry.core.3.result.row.4.value": { zh: "修剪指甲", en: "Nail trim" },
  "useCases.industry.expand.1.title": { zh: "教育培训", en: "Education & training" },
  "useCases.industry.expand.1.desc": { zh: "课程咨询、试听预约、报名登记", en: "Course inquiry, trial booking, enrollment" },
  "useCases.industry.expand.2.title": { zh: "上门服务 / 维修", en: "Field service / repair" },
  "useCases.industry.expand.2.desc": { zh: "工单创建、地址采集、时间安排", en: "Ticket creation, address capture, scheduling" },
  "useCases.industry.expand.3.title": { zh: "酒店 / 民宿 / 租赁", en: "Hospitality / rental" },
  "useCases.industry.expand.3.desc": { zh: "可用性查询、预订请求、客户登记", en: "Availability check, booking request, guest registration" },
  "useCases.industry.expand.4.title": { zh: "健身 / Wellness", en: "Fitness / wellness" },
  "useCases.industry.expand.4.desc": { zh: "课程预约、会员咨询、私教排期", en: "Class booking, membership inquiry, coach scheduling" },
  "useCases.industry.expand.5.title": { zh: "本地服务业", en: "Local services" },
  "useCases.industry.expand.5.desc": { zh: "电话咨询、预约、表单提交、客户跟进", en: "Phone inquiry, booking, form submission, follow-up" },
  "useCases.industry.expand.6.title": { zh: "工单型服务", en: "Ticket-based service" },
  "useCases.industry.expand.6.desc": { zh: "需求登记、任务分派、进度同步", en: "Request capture, task routing, status syncing" },

  // Industries
  "ind.label": { zh: "市场与行业", en: "Markets & Industries" },
  "ind.title": { zh: "ClawEase 的服务范围", en: "Where ClawEase operates" },
  "ind.markets.label": { zh: "重点调研市场：", en: "Target markets under research:" },
  "ind.us": { zh: "美国", en: "United States" },
  "ind.us.channels": { zh: "电话 · Email · 表单 · SMS", en: "Phone · Email · Forms · SMS" },
  "ind.hk": { zh: "香港", en: "Hong Kong" },
  "ind.hk.channels": { zh: "WhatsApp · 电话 · 表单 · IG", en: "WhatsApp · Phone · Forms · IG" },
  "ind.jp": { zh: "日本", en: "Japan" },
  "ind.jp.channels": { zh: "电话 · LINE · Web 预约", en: "Phone · LINE · Web Booking" },
  "ind.au": { zh: "澳大利亚", en: "Australia" },
  "ind.au.channels": { zh: "电话 · 表单 · IG DM · SMS", en: "Phone · Forms · IG DM · SMS" },
  "ind.core.title": { zh: "核心聚焦行业", en: "Core Target Industries" },
  "ind.expand.title": { zh: "可扩展行业", en: "Expandable Industries" },
  "ind.expand.1": { zh: "教育培训", en: "Education & Training" },
  "ind.expand.1.desc": { zh: "课程咨询、试听预约、报名登记", en: "Course inquiry, trial booking, enrollment" },
  "ind.expand.2": { zh: "上门服务 / 维修", en: "Field Service / Repair" },
  "ind.expand.2.desc": { zh: "工单创建、地址采集、时间安排", en: "Work order creation, address capture, scheduling" },
  "ind.expand.3": { zh: "酒店 / 民宿 / 租赁", en: "Hospitality / Rental" },
  "ind.expand.3.desc": { zh: "可用性查询、预订请求、客户登记", en: "Availability check, booking request, guest registration" },
  "ind.expand.4": { zh: "健身 / Wellness", en: "Fitness / Wellness" },
  "ind.expand.4.desc": { zh: "课程预约、会员咨询、私教排期", en: "Class booking, membership inquiry, PT scheduling" },
  "ind.expand.5": { zh: "本地服务业", en: "Local Services" },
  "ind.expand.5.desc": { zh: "电话咨询、预约、表单提交、客户跟进", en: "Phone inquiry, booking, form submission, follow-up" },

  // Stats
  "stats.1.num": { zh: "24/7", en: "24/7" },
  "stats.1.label": { zh: "全渠道全天候覆盖", en: "Always-on coverage across all channels" },
  "stats.2.num": { zh: "0", en: "0" },
  "stats.2.label": { zh: "API 对接需求——操作现有网页系统即可", en: "API integrations required — operates your existing web systems" },
  "stats.3.num": { zh: "100%", en: "100%" },
  "stats.3.label": { zh: "标准化咨询处理，每次一致", en: "Consistent, standardized inquiry handling — every single time" },

  // Pricing
  "pricing.label": { zh: "定价", en: "Pricing" },
  "pricing.title": { zh: "选择适合业务深度的套餐", en: "Choose the right level of automation" },
  "pricing.sub": {
    zh: "从免费体验开始，随着业务增长升级。Free 提供日常咨询与预约能力，Pro 和 Team 适合持续业务与团队协作。",
    en: "Start free and upgrade as you grow. Free covers everyday inquiries and booking, while Pro and Team support ongoing business and collaboration.",
  },
  "pricing.billing.monthly": { zh: "月付", en: "Monthly" },
  "pricing.billing.annual": { zh: "年付", en: "Annual" },
  "pricing.billing.save": { zh: "省 20%", en: "Save 20%" },
  "pricing.popular": { zh: "最受欢迎", en: "Most popular" },
  "pricing.perMonth": { zh: "/月", en: "/mo" },
  "pricing.monthly.note": { zh: "按月支付，可随时取消。", en: "Billed monthly. Cancel anytime." },
  "pricing.annual.note": { zh: "按年支付，月费 8 折。", en: "Annual plan, 20% off monthly pricing." },
  "pricing.free.name": { zh: "Free", en: "Free" },
  "pricing.free.summary": {
    zh: "零成本体验 ClawEase，用赠送额度完成真实的线上咨询流程。",
    en: "Explore ClawEase at no cost with credits you can use on real online inquiry workflows.",
  },
  "pricing.free.note": { zh: "无需订阅，立即体验 ClawEase。", en: "No subscription required. Start exploring ClawEase now." },
  "pricing.free.cta": { zh: "下载", en: "Download" },
  "pricing.free.feature.1": { zh: "首次注册送 $20 永久额度", en: "$20 permanent welcome credits" },
  "pricing.free.feature.1.note": { zh: "≈ 5,000 条消息 / 200 分钟语音", en: "≈ 5,000 messages / 200 voice min" },
  "pricing.free.feature.1.detail": {
    zh: "首次创建账号即可领取，额度永久有效；",
    en: "Added when you create your first account and never expires;",
  },
  "pricing.free.feature.1.rate": { zh: "可抵扣任意 AI 费用。", en: "Can offset any AI costs." },
  "pricing.free.feature.2": { zh: "每月赠送 $5 月额度", en: "$5 monthly credits" },
  "pricing.free.feature.2.detail": {
    zh: "同样可抵扣任意 AI 费用。",
    en: "Can also offset any AI costs.",
  },
  "pricing.free.feature.3": { zh: "邮件、电话与网页在线自动预约", en: "Automated booking by email, phone, and web" },
  "pricing.free.feature.3.detail": {
    zh: "自动回复客户咨询，并通过邮件、电话和网页引导客户完成预约。",
    en: "Automatically answers inquiries and helps customers book through email, phone, and web.",
  },
  "pricing.free.feature.4": { zh: "网站在线聊天", en: "Website live chat" },
  "pricing.free.feature.4.detail": {
    zh: "通过专属网页咨询链接，与客户进行实时对话。",
    en: "Chat with customers in real time through a dedicated web link.",
  },
  "pricing.free.feature.5": { zh: "知识库", en: "Knowledge base" },
  "pricing.free.feature.5.detail": {
    zh: "添加业务资料，让 AI 根据你的内容回答客户问题。",
    en: "Add your business information so AI can answer customer questions.",
  },
  "pricing.free.feature.6": { zh: "自带预约功能", en: "Built-in appointment booking" },
  "pricing.free.feature.6.detail": {
    zh: "客户可以直接选择时间并提交预约。",
    en: "Customers can choose a time and submit a booking directly.",
  },
  "pricing.free.feature.7": { zh: "支持中文和英语", en: "Supports English and Chinese" },
  "pricing.free.feature.7.detail": {
    zh: "面向中英文客户自动回答问题并完成预约。",
    en: "Answer questions and complete bookings for English- and Chinese-speaking customers.",
  },
  "pricing.pro.name": { zh: "Pro", en: "Pro" },
  "pricing.pro.summary": {
    zh: "适合持续处理真实业务，把线上咨询推进为可预约线索。",
    en: "For teams ready to turn ongoing online inquiries into booking-ready requests.",
  },
  "pricing.pro.cta": { zh: "下载", en: "Download" },
  "pricing.pro.feature.1": { zh: "每月包含 $49 使用额度", en: "$49 in usage credits every month" },
  "pricing.pro.feature.1.detail": {
    zh: "满足大多数业务的日常预约需求。",
    en: "Covers the everyday booking needs of most businesses.",
  },
  "pricing.pro.feature.2": { zh: "包含 Free 全部能力", en: "Everything in Free" },
  "pricing.pro.feature.2.detail": {
    zh: "并提供适合持续业务使用的完整线上自动化。",
    en: "Plus complete online automation for ongoing business use.",
  },
  "pricing.pro.feature.3": { zh: "24/7 线上响应", en: "24/7 online response" },
  "pricing.pro.feature.3.detail": {
    zh: "非营业时间也能接住线上请求。",
    en: "Captures online requests outside business hours.",
  },
  "pricing.pro.feature.4": { zh: "线索资格判断", en: "Lead qualification" },
  "pricing.pro.feature.4.detail": {
    zh: "收集需求、时间偏好和客户意向。",
    en: "Collects need, timing, and customer intent.",
  },
  "pricing.pro.feature.5": { zh: "可预约请求整理", en: "Booking-ready requests" },
  "pricing.pro.feature.5.detail": {
    zh: "把零散对话整理成待确认线索。",
    en: "Turns scattered replies into clear next steps.",
  },
  "pricing.pro.feature.6": { zh: "短信确认", en: "SMS confirmations" },
  "pricing.pro.feature.6.detail": {
    zh: "用于基础预约确认和提醒。",
    en: "For basic booking confirmation and reminders.",
  },
  "pricing.pro.feature.7": { zh: "完整对话记录", en: "Full conversation history" },
  "pricing.pro.feature.8": { zh: "支持 25 个语种", en: "Supports 25 languages" },
  "pricing.pro.feature.8.detail": {
    zh: "面向多语言客户提供一致的咨询体验。",
    en: "Delivers a consistent inquiry experience across languages.",
  },
  "pricing.pro.feature.9": { zh: "WhatsApp 等更多预约渠道支持", en: "WhatsApp and more booking channels" },
  "pricing.pro.feature.9.detail": {
    zh: "接入 WhatsApp，并通过自定义渠道连接其他通信入口。",
    en: "Connect WhatsApp and additional communication endpoints through custom channels.",
  },
  "pricing.pro.feature.10": { zh: "自动跟进", en: "Automatic follow-up" },
  "pricing.pro.feature.10.detail": {
    zh: "咨询后自动继续联系客户，推进确认与转化。",
    en: "Automatically follows up after an inquiry to keep customers moving forward.",
  },
  "pricing.pro.feature.11": { zh: "智能排班", en: "Smart scheduling" },
  "pricing.pro.feature.11.detail": {
    zh: "根据时间、服务和人员安排预约。",
    en: "Schedules appointments around time, service, and staff availability.",
  },
  "pricing.standard.name": { zh: "Standard", en: "Standard" },
  "pricing.standard.label": { zh: "线上咨询自动化", en: "Online inquiry automation" },
  "pricing.standard.summary": {
    zh: "接入现有预约系统，把线上咨询整理成可预约线索和基础预约。",
    en: "Connects to your booking system and turns online inquiries into booking-ready requests.",
  },
  "pricing.standard.feature.1": {
    zh: "线上咨询自动化",
    en: "Online inquiry automation",
  },
  "pricing.standard.feature.1.detail": {
    zh: "覆盖消息、邮件、网站表单等入口。",
    en: "Covers messages, email, and website forms.",
  },
  "pricing.standard.feature.9": { zh: "24/7 线上响应", en: "24/7 online response" },
  "pricing.standard.feature.9.detail": {
    zh: "非营业时间也能接住线上请求。",
    en: "Captures online requests outside business hours.",
  },
  "pricing.standard.feature.2": {
    zh: "线索资格判断",
    en: "Lead qualification",
  },
  "pricing.standard.feature.2.detail": {
    zh: "收集需求、时间偏好和客户意向。",
    en: "Collects need, timing, and customer intent.",
  },
  "pricing.standard.feature.3": {
    zh: "可预约请求整理",
    en: "Booking-ready requests",
  },
  "pricing.standard.feature.3.detail": {
    zh: "把零散对话整理成待确认线索。",
    en: "Turns scattered replies into clear next steps.",
  },
  "pricing.standard.feature.4": { zh: "500 次沟通 / 月", en: "500 conversations / month" },
  "pricing.standard.feature.4.detail": {
    zh: "支持 25 个语种的线上对话。",
    en: "Supports online conversations in 25 languages.",
  },
  "pricing.standard.feature.5": { zh: "短信确认", en: "SMS confirmations" },
  "pricing.standard.feature.5.detail": { zh: "用于基础预约确认和提醒。", en: "For basic booking confirmation and reminders." },
  "pricing.standard.feature.7": { zh: "对话记录", en: "Conversation history" },
  "pricing.standard.feature.8": { zh: "30 天免费试用", en: "30-day free trial" },
  "pricing.premium.name": { zh: "Team", en: "Team" },
  "pricing.premium.label": { zh: "团队协作", en: "Team collaboration" },
  "pricing.premium.summary": {
    zh: "适合多人协作的团队，共享额度、客户沟通和业务结果。",
    en: "For teams that need shared credits, customer conversations, and business results.",
  },
  "pricing.premium.cta": { zh: "下载", en: "Download" },
  "pricing.premium.feature.1": { zh: "包含 Pro 全部能力", en: "Everything in Pro" },
  "pricing.premium.feature.1.detail": {
    zh: "包含多语言、多渠道和自动跟进等能力。",
    en: "Includes multilingual, multi-channel, and automatic follow-up capabilities.",
  },
  "pricing.premium.feature.2": { zh: "专业 PMS 对接与预约系统", en: "Professional PMS integration and booking system" },
  "pricing.premium.feature.2.detail": {
    zh: "支持专业 PMS 对接，并提供专业版预约管理系统。",
    en: "Connects with professional PMS platforms and includes an advanced booking management system.",
  },
  "pricing.premium.feature.3": { zh: "无限沟通次数", en: "Unlimited conversations" },
  "pricing.premium.feature.3.detail": {
    zh: "支持 25 个语种的全渠道沟通。",
    en: "Supports conversations in 25 languages across channels.",
  },
  "pricing.premium.feature.4": { zh: "自动跟进", en: "Automatic follow-up" },
  "pricing.premium.feature.4.detail": { zh: "咨询后继续推进确认与转化。", en: "Keeps requests moving after first contact." },
  "pricing.premium.feature.5": { zh: "智能排班", en: "Smart scheduling" },
  "pricing.premium.feature.5.detail": { zh: "按时间、服务和资源生成安排。", en: "Coordinates time, service, and availability." },
  "pricing.premium.feature.6": {
    zh: "多渠道确认",
    en: "Multi-channel confirmations",
  },
  "pricing.premium.feature.6.detail": {
    zh: "支持短信、WhatsApp 和 Email。",
    en: "Supports SMS, WhatsApp, and email.",
  },
  "pricing.premium.feature.7": { zh: "智能分析与团队通知", en: "Analytics and team notifications" },
  "pricing.premium.feature.7.detail": {
    zh: "输出关键结果和执行摘要，并同步给团队成员。",
    en: "Reports key outcomes and summaries, then keeps the team informed.",
  },
  "pricing.premium.feature.8": { zh: "团队通知", en: "Team notifications" },
  "pricing.premium.feature.9": { zh: "每月包含 $119 使用额度", en: "$119 in usage credits every month" },
  "pricing.premium.feature.9.detail": {
    zh: "团队成员共享使用额度。",
    en: "Usage credits are shared across the team.",
  },
  "pricing.premium.feature.10": { zh: "支持 3 个团队账号", en: "3 team accounts" },
  "pricing.premium.feature.10.detail": {
    zh: "3 位团队成员可以共享使用和协作。",
    en: "Three team members can share access and collaborate.",
  },

  // CTA
  "cta.label": { zh: "联系我们", en: "CONTACT US" },
  "cta.title.base": { zh: "准备好不再错过", en: "Ready to stop missing" },
  "cta.title.accent": { zh: "高价值客户请求？", en: "high-value requests?" },
  "cta.sub": {
    zh: "预约 15 分钟产品演示，看看 ClawEase 如何接住你的真实咨询量，并把请求推进到系统里的确定结果。",
    en: "Book a 15-minute demo and see how ClawEase handles your real inquiry volume, then turns requests into confirmed outcomes inside your systems.",
  },
  "cta.button.primary": { zh: "预约 15 分钟演示", en: "Book a 15-min demo" },
  "cta.button.secondary": { zh: "提交需求", en: "Send request" },
  "cta.button": { zh: "免费试用", en: "Get started for free" },

  // Lead form
  "lead.back": { zh: "返回官网", en: "Back to site" },
  "lead.badge": { zh: "预约演示", en: "Book a Demo" },
  "lead.title": { zh: "联系我们", en: "Contact Us" },
  "lead.sub": {
    zh: "告诉我们你的联系信息和业务场景，ClawEase 团队会尽快联系你，确认渠道、系统和演示安排。",
    en: "Share your contact details and use case. The ClawEase team will follow up to confirm your channels, systems, and demo schedule.",
  },
  "lead.incentive": {
    zh: "完成产品演示，即可获得 50 美元 Amazon 礼品卡。",
    en: "Get a $50 Amazon gift card when you take a demo.",
  },
  "lead.name": { zh: "姓名", en: "Name" },
  "lead.name.placeholder": { zh: "请输入你的姓名", en: "Your name" },
  "lead.email": { zh: "邮箱", en: "Email" },
  "lead.email.placeholder": { zh: "name@company.com", en: "name@company.com" },
  "lead.phone": { zh: "手机号", en: "Phone number" },
  "lead.country": { zh: "选择国家或地区", en: "Select country or region" },
  "lead.phone.placeholder": { zh: "请输入电话号码（选填）", en: "Phone number (optional)" },
  "lead.company": { zh: "公司", en: "Company" },
  "lead.company.placeholder": { zh: "公司或机构名称（选填）", en: "Company or organization (optional)" },
  "lead.note": { zh: "备注信息", en: "Notes" },
  "lead.note.placeholder": {
    zh: "可以简单描述你的业务场景，例如渠道、系统、预约/咨询流程等。",
    en: "Briefly describe your use case, such as channels, systems, or inquiry and booking workflows.",
  },
  "lead.defaultContent": {
    zh: "官网联系表单提交，希望了解 ClawEase 演示和接入方案。",
    en: "Website contact form submission. Interested in a ClawEase demo and onboarding plan.",
  },
  "lead.privacy": {
    zh: "提交后，我们会尽快联系你。你的信息仅用于回复咨询、安排演示及 ClawEase 购买/接入沟通，不用于无关用途。",
    en: "After submission, we will contact you shortly. Your information is used only to respond to your inquiry, arrange a demo, and discuss ClawEase purchase or onboarding needs.",
  },
  "lead.officialX": { zh: "官方 X：", en: "Official X:" },
  "lead.submit": { zh: "提交需求", en: "Submit request" },
  "lead.submitting": { zh: "提交中...", en: "Submitting..." },
  "lead.success.title": { zh: "已收到需求", en: "Request received" },
  "lead.success.desc": { zh: "我们会尽快联系你，确认业务场景和演示安排。", en: "We will contact you shortly to confirm your business scenario and demo plan." },
  "lead.error.name": { zh: "请填写姓名", en: "Please enter your name" },
  "lead.error.email": { zh: "请填写有效邮箱", en: "Please enter a valid email" },
  "lead.error.phone": { zh: "请填写有效手机号", en: "Please enter a valid phone number" },
  "lead.error.submit": {
    zh: "提交失败，可能是后端服务暂未配置。请稍后重试，或直接邮件联系 info@clawease.ai。",
    en: "Submission failed. The backend may not be configured yet. Please try again later or email info@clawease.ai.",
  },

  // 404
  "notFound.badge": { zh: "页面不存在", en: "Page not found" },
  "notFound.title": { zh: "这个页面走丢了", en: "This page does not exist" },
  "notFound.desc": {
    zh: "你访问的地址可能已失效，或页面暂未发布。可以回到官网继续浏览，或提交信息联系我们。",
    en: "The address may be outdated, or the page has not been published yet. Return to the site or leave your details and we will help.",
  },
  "notFound.home": { zh: "返回首页", en: "Back to home" },
  "notFound.contact": { zh: "联系我们", en: "Contact us" },

  // Footer
  "footer.tagline": { zh: "面向高价值客户请求的 AI 业务操作员", en: "AI business operator for high-value customer requests" },
  "footer.desc.1": {
    zh: "面向高价值请求的 AI 业务操作员",
    en: "AI business operator for high-value requests",
  },
  "footer.desc.2": {
    zh: "让企业从倾听、识别、办理到同步，形成完整业务闭环。",
    en: "Help teams listen, understand, execute, and sync every workflow end to end.",
  },
  "footer.legal.privacy": { zh: "隐私政策", en: "Privacy Policy" },
  "footer.legal.cookie": { zh: "Cookie 政策", en: "Cookie Policy" },
  "footer.legal.terms": { zh: "使用条款", en: "Terms of Use" },
  "footer.legal.about": { zh: "关于我们", en: "About Us" },
  "footer.contact.title": { zh: "联系我们", en: "Contact Us" },
  "footer.contact.email": { zh: "邮箱", en: "Email" },
  "footer.contact.x": { zh: "官方 X", en: "Official X" },
  "footer.copyright": {
    zh: "Copyright © 2026 MytePro Technology Limited. All rights reserved.",
    en: "Copyright © 2026 MytePro Technology Limited. All rights reserved.",
  },

  // Cookie consent
  "cookieConsent.title": { zh: "Cookie 使用选择", en: "Cookie preferences" },
  "cookieConsent.desc": {
    zh: "我们使用可选 Cookie 和类似技术来了解网站访问情况、衡量广告效果并改进你的体验。你可以接受或拒绝这些可选 Cookie。",
    en: "We use optional cookies and similar technologies to understand website traffic, measure ad performance, and improve your experience. You can accept or reject these optional cookies at any time.",
  },
  "cookieConsent.policy": { zh: "查看 Cookie 政策", en: "View Cookie Policy" },
  "cookieConsent.reject": { zh: "拒绝", en: "Reject" },
  "cookieConsent.accept": { zh: "允许", en: "Allow" },
};
